import type { File } from "lucide-react";
import {
  Activity,
  ArrowUp,
  BarChart3,
  Bot,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GitBranch,
  LoaderCircle,
  LogOut,
  PanelRightOpen,
  Plus,
  RefreshCw,
  Settings,
  ShieldCheck,
  Square,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AdminModelCacheUsage } from "./AdminModelCacheUsage";
import { GuideApprovalReview } from "./GuideApprovalReview";
import type { FormEvent } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { TaskEventKinds } from "@rnavoyager/contracts";
import type { FileVersion, ResultDocument, SessionReportResponse } from "@rnavoyager/contracts";

import { ActionApprovalDetails } from "./ActionApprovalDetails";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import {
  ConversationModelSelect,
  conversationModelChoices,
  defaultConversationModelChoice,
} from "./ConversationModelSelect";
import {
  WorkspaceSidebar,
  TaskWorkspaceHeader,
  ChatMessage,
  TaskResultCard,
  TaskPlanCardView,
} from "./ChatWorkspaceParts";
import { BootstrapAdministratorScreen } from "./BootstrapAdministratorScreen";
import { AssistantActivity, assistantActivityLabel } from "./AssistantActivity";
import { isPlanSnapshotEvent } from "./plan-events";
import {
  type ComposerAttachment,
  ComposerAttachmentList,
  FileDropOverlay,
  ComposerUploadButton,
  createComposerAttachments,
  useComposerDropZone,
} from "./ComposerAttachments";
import type { ResultSelection } from "./TaskResultDocument";
import {
  SessionFileLink,
  SessionMarkdown,
  SessionFileReferencesContext,
} from "./SessionFileReferences";
import { ResultWorkspaceRail } from "./ResultWorkspace";
import { MissingReportNotice, ReportActionsContext } from "./ReportActions";
import { SessionUsageDialog } from "./SessionUsageDialog";
import {
  type ActionApprovalDecision,
  ApiError,
  answerTaskTurn,
  changeOwnPassword,
  changeUserPassword,
  confirmAccountRawArtifactConsent,
  createSession,
  createConversation,
  recoverConversation,
  waitForSessionReady,
  createTaskTurn,
  activateUser,
  deleteSession,
  deleteUser,
  deleteWorkspaceInput,
  decideAction,
  discardPlan,
  getCurrentUser,
  getAuthOptions,
  getAdminUsage,
  getMcpRawUsage,
  getMyUsage,
  getRuntimeOptions,
  getSession,
  getTaskEvents,
  getSessionReport,
  interruptTask,
  listAdminGuides,
  listGuides,
  listSessions,
  listUsers,
  login,
  logout,
  resumeTask,
  retrySessionReport,
  register,
  selectGuide,
  startPlan,
  taskEventsStreamUrl,
  updateSessionSeries,
  uploadWorkspaceInput,
} from "./api";
import { executionStatusSummary, WorkspaceRightRail } from "./WorkspaceRightRail";
import { TerminationAdmin } from "./TerminationAdmin";
import { AppPaths, correctedAppPath, replaceAppPath } from "./app-route";
import DemoChat, { U1A_DEMO_PATH } from "./DemoChat";
import { DemoContinuationScreen } from "./DemoContinuation";
import type { ContinuedDemo } from "./DemoContinuation";
import { DemoHistory, DemoHistoryScope, useDemoHistory } from "./DemoHistory";
import { readPendingDemo, clearPendingDemo } from "./demo-state";
import type {
  AdminGuidesResponse,
  AuthOptions,
  AuthUser,
  GuideSummary,
  MessageAttachment,
  McpRawUsage,
  RuntimeOptions,
  SessionDetail,
  SessionSummary,
  TaskEvent,
  TaskRuntimeState,
  UsageOverview,
  UserUsage,
  UserRole,
} from "./types";

const ACTIVE_STATES = new Set(["preparing", "planning", "executing", "running", "stopping"]);
// 审批 HTTP 响应可能早于 approval_granted 事件；等待审批时仍须持续同步，但不改变执行中 UI 语义。
const EVENT_SYNC_STATES = new Set([...ACTIVE_STATES, "awaiting_action_approval", "stop_failed"]);
const RESUMABLE_STATES = new Set(["cancelled", "failed"]);
const INPUT_MUTABLE_STATES = new Set(["idle", "completed", "failed", "cancelled"]);
const GUIDE_EDITABLE_STATES = new Set([
  "idle",
  "completed",
  "failed",
  "cancelled",
  "awaiting_plan_start",
]);
const MODEL_LOCKED_STATES = new Set([
  ...ACTIVE_STATES,
  "stop_failed",
  "awaiting_user_input",
  "awaiting_plan_start",
  "awaiting_action_approval",
]);
const COMPACT_RAIL_QUERY = "(max-width: 760px), (orientation: portrait) and (max-width: 1024px)";
const USER_ATTENTION_EXECUTION_STATUSES = new Set(["failed", "deadline_exceeded"]);

export default function App() {
  if (window.location.pathname === U1A_DEMO_PATH) return <DemoChat />;
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const [continuedDemo, setContinuedDemo] = useState<ContinuedDemo | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState("");
  const clearSession = useCallback(() => {
    replaceAppPath(AppPaths.login);
    setContinuedDemo(null);
    setUser(null);
  }, []);

  useEffect(() => {
    void getCurrentUser()
      .then(setUser)
      .catch((cause: unknown) => {
        if (!(cause instanceof ApiError && cause.status === 401)) {
          setError(messageOf(cause));
        }
      })
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    function handleAuthExpired(event: Event) {
      const message =
        event instanceof CustomEvent && typeof event.detail === "string"
          ? event.detail
          : "登录已过期，请重新登录。";
      clearSession();
      setError(message);
    }
    window.addEventListener("rnavoyager:auth-expired", handleAuthExpired);
    return () => window.removeEventListener("rnavoyager:auth-expired", handleAuthExpired);
  }, [clearSession]);

  useEffect(() => {
    if (booting) return;
    const corrected = correctedAppPath(window.location.pathname, user !== null);
    if (corrected) replaceAppPath(corrected);
  }, [booting, user]);

  if (booting) {
    return <LoadingScreen label="正在连接 RNAVoyager…" />;
  }
  if (!user) {
    return (
      <LoginScreen
        initialError={error}
        onLogin={(authenticatedUser) => {
          setError("");
          replaceAppPath(AppPaths.chat);
          setUser(authenticatedUser);
        }}
      />
    );
  }
  if (user.must_rotate_credentials) {
    return <BootstrapAdministratorScreen user={user} onCompleted={clearSession} />;
  }
  if (user.allow_raw_artifacts !== true) {
    return (
      <AccountRawArtifactConsentScreen
        user={user}
        onConfirmed={(updated) => {
          setError("");
          setUser(updated);
        }}
        onLogout={clearSession}
      />
    );
  }
  if (!continuedDemo && readPendingDemo())
    return <DemoContinuationScreen identityId={user.id} onReady={setContinuedDemo} />;
  return <Workspace user={user} onLogout={clearSession} initialDemo={continuedDemo ?? undefined} />;
}

function LoginScreen({
  initialError,
  onLogin,
}: {
  initialError: string;
  onLogin: (user: AuthUser) => void;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [gender, setGender] = useState<NonNullable<AuthUser["gender"]>>("undisclosed");
  const [notice, setNotice] = useState("");
  const [authOptions, setAuthOptions] = useState<AuthOptions | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [allowRawArtifacts, setAllowRawArtifacts] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(initialError);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void getAuthOptions()
      .then((options) => {
        if (active) setAuthOptions(options);
      })
      .catch(() => {
        // 登录不依赖公开配置接口；读取失败时仅隐藏自助注册入口。
      });
    return () => {
      active = false;
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (mode === "register") {
      const minimumLength = authOptions?.password_min_length ?? 12;
      if (password.length < minimumLength) {
        setError(`密码至少需要 ${minimumLength} 个字符`);
        return;
      }
      if (password !== passwordConfirmation) {
        setError("两次输入的密码不一致");
        return;
      }
      if (!name.trim() || !institution.trim()) {
        setError("请填写姓名和所在机构");
        return;
      }
    }
    setBusy(true);
    setError("");
    try {
      if (mode === "register") {
        await register({
          name: name.trim(),
          institution: institution.trim(),
          gender,
          email: normalizedEmail,
          password,
          allowRawArtifacts,
        });
        switchMode("login");
        setNotice("注册申请已提交。管理员审核通过后将开放账户使用权限，请耐心等待。");
      } else {
        onLogin(await login(normalizedEmail, password));
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  function switchMode(nextMode: "login" | "register") {
    setMode(nextMode);
    setPassword("");
    setPasswordConfirmation("");
    setAllowRawArtifacts(false);
    setShowPassword(false);
    setError("");
    setNotice("");
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand-mark">
          <Activity size={24} />
        </div>
        <p className="eyebrow">AGENTIC BIOINFORMATICS</p>
        <h1>RNAVoyager</h1>
        <p className="muted">
          {mode === "register"
            ? "填写注册资料，提交后请等待管理员审核通过并开放账户权限。"
            : "登录后，用自然语言规划并执行计算研究任务。"}
        </p>
        <form onSubmit={(event) => void submit(event)} className="stack">
          {mode === "register" && (
            <>
              <label>
                姓名
                <input
                  aria-label="姓名"
                  autoComplete="name"
                  required
                  maxLength={100}
                  disabled={busy}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                所在机构
                <input
                  aria-label="所在机构"
                  autoComplete="organization"
                  required
                  maxLength={200}
                  disabled={busy}
                  value={institution}
                  onChange={(event) => setInstitution(event.target.value)}
                />
              </label>
              <label>
                性别
                <select
                  aria-label="性别"
                  disabled={busy}
                  value={gender}
                  onChange={(event) =>
                    setGender(event.target.value as NonNullable<AuthUser["gender"]>)
                  }
                >
                  <option value="undisclosed">不愿透露</option>
                  <option value="female">女</option>
                  <option value="male">男</option>
                  <option value="other">其他</option>
                </select>
              </label>
            </>
          )}
          <label>
            邮箱
            <input
              aria-label="邮箱"
              type="email"
              autoComplete="username"
              required
              disabled={busy}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            密码
            <span className="password-input">
              <input
                aria-label="密码"
                type={showPassword ? "text" : "password"}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                required
                disabled={busy}
                minLength={
                  mode === "register" ? (authOptions?.password_min_length ?? 12) : undefined
                }
                maxLength={authOptions?.password_max_length ?? 128}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                aria-label={showPassword ? "隐藏密码" : "显示密码"}
                className="password-visibility"
                disabled={busy}
                type="button"
                onClick={() => setShowPassword((current) => !current)}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </span>
          </label>
          {mode === "register" && (
            <label>
              确认密码
              <input
                aria-label="确认密码"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                disabled={busy}
                minLength={authOptions?.password_min_length ?? 12}
                maxLength={authOptions?.password_max_length ?? 128}
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
              />
            </label>
          )}
          {mode === "register" && (
            <label className="account-consent-option">
              <span className="setting-checkbox-row">
                <input
                  aria-label="同意账号级原始内容披露"
                  checked={allowRawArtifacts}
                  disabled={busy}
                  type="checkbox"
                  onChange={(event) => setAllowRawArtifacts(event.target.checked)}
                />
                <span>
                  我明确同意：使用 RNAVoyager
                  时，我输入的文本和上传的文件可按任务需要发送给所选模型或工具服务商。此授权对本账号后续会话持续有效。
                </span>
              </span>
            </label>
          )}
          {mode === "register" && (
            <p className="auth-hint">
              密码需包含至少 {authOptions?.password_min_length ?? 12} 个字符。
              数据披露授权可独立选择，默认不勾选。
            </p>
          )}
          {error && <ErrorBanner message={error} />}
          {notice && (
            <p className="auth-hint" role="status">
              {notice}
            </p>
          )}
          <button
            className="primary"
            disabled={
              busy ||
              !email.trim() ||
              !password ||
              (mode === "register" && (!name.trim() || !institution.trim()))
            }
          >
            {busy
              ? mode === "register"
                ? "正在提交申请…"
                : "正在登录…"
              : mode === "register"
                ? "提交注册申请"
                : "登录"}
          </button>
        </form>
        {authOptions?.registration_enabled ? (
          <p className="auth-switch">
            {mode === "register" ? "已有账户？" : "还没有账户？"}
            <button
              type="button"
              disabled={busy}
              onClick={() => switchMode(mode === "register" ? "login" : "register")}
            >
              {mode === "register" ? "返回登录" : "创建账户"}
            </button>
          </p>
        ) : authOptions ? (
          <p className="auth-hint auth-policy">自助注册暂未开放，请联系管理员开放注册。</p>
        ) : null}
        {mode === "login" && <p className="auth-recovery">忘记密码？请联系平台管理员重置。</p>}
        {readPendingDemo() && (
          <p className="auth-hint">
            <a href={U1A_DEMO_PATH}>返回研究案例</a>
          </p>
        )}
      </section>
    </main>
  );
}

function AccountRawArtifactConsentScreen({
  user,
  onConfirmed,
  onLogout,
}: {
  user: AuthUser;
  onConfirmed: (user: AuthUser) => void;
  onLogout: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!confirmed) return;
    setBusy(true);
    setError("");
    try {
      onConfirmed(await confirmAccountRawArtifactConsent());
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-card account-consent-card">
        <div className="brand-mark">
          <ShieldCheck size={24} />
        </div>
        <p className="eyebrow">一次性账号授权</p>
        <h1>确认数据披露范围</h1>
        <p className="muted">
          账号 {user.email} 创建于旧版本。完成这一次确认后，后续会话不会再要求手动授权。
        </p>
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <label className="account-consent-option">
            <span className="setting-checkbox-row">
              <input
                aria-label="同意账号级原始内容披露"
                checked={confirmed}
                disabled={busy}
                required
                type="checkbox"
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              <span>
                我明确同意：使用 RNAVoyager
                时，我输入的文本和上传的文件可按任务需要发送给所选模型或工具服务商。此授权对本账号后续会话持续有效。
              </span>
            </span>
          </label>
          {error && <ErrorBanner message={error} />}
          <button className="primary" disabled={busy || !confirmed}>
            {busy ? "正在记录授权…" : "同意并进入 RNAVoyager"}
          </button>
          <button className="secondary" disabled={busy} type="button" onClick={onLogout}>
            退出登录
          </button>
        </form>
      </section>
    </main>
  );
}

function Workspace({
  user,
  onLogout,
  initialDemo,
}: {
  user: AuthUser;
  onLogout: () => void;
  initialDemo?: ContinuedDemo;
}) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialDemo?.session.id ?? null);
  const [session, setSession] = useState<SessionDetail | null>(initialDemo?.session ?? null);
  const [options, setOptions] = useState<RuntimeOptions | null>(null);
  const [guides, setGuides] = useState<GuideSummary[]>([]);
  const [adminView, setAdminView] = useState(user.role === "admin" && !initialDemo);
  const [usageView, setUsageView] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [newConversationGoal, setNewConversationGoal] = useState("");
  const [newConversationAttachments, setNewConversationAttachments] = useState<
    ComposerAttachment[]
  >([]);
  const newConversationDraftRef = useRef<SessionDetail | null>(null);
  const newConversationGenerationRef = useRef(0);
  const newConversationDraftPromiseRef = useRef<{
    generation: number;
    promise: Promise<SessionDetail>;
  } | null>(null);
  const newConversationUploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const newConversationUploadControllersRef = useRef(new Map<string, AbortController>());
  const removedNewConversationAttachmentIdsRef = useRef(new Set<string>());
  const [launchingGoal, setLaunchingGoal] = useState<string | null>(null);
  // 普通用户需要先取得模型目录；初始化期间不要短暂暴露一个不可输入的任务表单。
  const [busy, setBusy] = useState(user.role === "user");
  const [error, setError] = useState("");

  useEffect(
    () => () => {
      for (const controller of newConversationUploadControllersRef.current.values()) {
        controller.abort();
      }
    },
    [],
  );

  const refreshSessions = useCallback(async () => {
    const rows = await listSessions();
    setSessions(rows);
    setSelectedId((current) => {
      if (current && rows.some((item) => item.id === current)) return current;
      return null;
    });
  }, []);

  useEffect(() => {
    if (user.role === "admin" && !initialDemo) {
      setAdminView(true);
      setBusy(false);
      return;
    }
    setBusy(true);
    Promise.all([
      refreshSessions(),
      getRuntimeOptions(),
      listGuides(),
      (initialDemo ? Promise.resolve(initialDemo.session) : recoverConversation(user.id)).catch(
        (cause) => {
          setError(messageOf(cause));
          return null;
        },
      ),
    ])
      .then(([, runtimeOptions, approvedGuides, recovered]) => {
        setOptions(runtimeOptions);
        setGuides(approvedGuides);
        if (recovered) {
          setSelectedId(recovered.id);
          setSession(recovered);
          setSessions((current) => [
            recovered,
            ...current.filter((item) => item.id !== recovered.id),
          ]);
        }
        // 与 options 同一批提交，避免短暂渲染一个“已出现但仍禁用”的新任务表单。
        setBusy(false);
      })
      .catch((cause) => {
        setError(messageOf(cause));
        setBusy(false);
      });
  }, [refreshSessions, user.role, user.id, initialDemo]);

  useEffect(() => {
    if (!selectedId || adminView || usageView) {
      setSession(null);
      return;
    }
    void getSession(selectedId)
      .then(setSession)
      .catch((cause) => setError(messageOf(cause)));
  }, [selectedId, adminView, usageView]);

  function openNewConversation() {
    newConversationGenerationRef.current += 1;
    for (const controller of newConversationUploadControllersRef.current.values()) {
      controller.abort();
    }
    newConversationUploadControllersRef.current.clear();
    removedNewConversationAttachmentIdsRef.current.clear();
    setNewConversationGoal("");
    setNewConversationAttachments([]);
    newConversationDraftRef.current = null;
    newConversationDraftPromiseRef.current = null;
    setLaunchingGoal(null);
    setSelectedId(null);
    setSession(null);
    setAdminView(false);
    setUsageView(false);
    setMobileSidebarOpen(false);
    setError("");
  }

  function addNewConversationFiles(files: File[]) {
    if (!options) return;
    const accepted = validComposerFiles(
      files,
      options.workspace_upload,
      newConversationAttachments.length,
      newConversationAttachments.reduce((total, item) => total + item.file.size, 0),
    );
    if (accepted.error) setError(accepted.error);
    if (accepted.files.length > 0) {
      const items = createComposerAttachments(accepted.files);
      setNewConversationAttachments((current) => [...current, ...items]);
      scheduleNewConversationUploads(items);
    }
  }

  function ensureNewConversationDraft(): Promise<SessionDetail> {
    if (newConversationDraftRef.current) {
      return Promise.resolve(newConversationDraftRef.current);
    }
    const generation = newConversationGenerationRef.current;
    if (newConversationDraftPromiseRef.current?.generation !== generation) {
      const promise = createSession()
        .then((created) => {
          if (newConversationGenerationRef.current === generation) {
            newConversationDraftRef.current = created;
            void refreshSessions();
          }
          return created;
        })
        .finally(() => {
          if (newConversationDraftPromiseRef.current?.promise === promise) {
            newConversationDraftPromiseRef.current = null;
          }
        });
      newConversationDraftPromiseRef.current = { generation, promise };
    }
    return newConversationDraftPromiseRef.current.promise;
  }

  function scheduleNewConversationUploads(items: ComposerAttachment[]) {
    const generation = newConversationGenerationRef.current;
    newConversationUploadQueueRef.current = newConversationUploadQueueRef.current
      .then(async () => {
        let draft: SessionDetail;
        try {
          draft = await ensureNewConversationDraft();
          draft = await waitForSessionReady(draft.id);
        } catch (cause) {
          const message = messageOf(cause);
          setNewConversationAttachments((current) =>
            current.map((item) =>
              items.some((candidate) => candidate.id === item.id)
                ? { ...item, status: "failed", error: message }
                : item,
            ),
          );
          setError(message);
          return;
        }
        if (newConversationGenerationRef.current !== generation) return;
        for (const attachment of items) {
          if (newConversationGenerationRef.current !== generation) return;
          if (removedNewConversationAttachmentIdsRef.current.has(attachment.id)) continue;
          const controller = new AbortController();
          newConversationUploadControllersRef.current.set(attachment.id, controller);
          setNewConversationAttachments((current) =>
            current.map((item) =>
              item.id === attachment.id
                ? { ...item, status: "uploading", progress: 0, error: undefined }
                : item,
            ),
          );
          try {
            const uploaded = await uploadWorkspaceInput(draft.id, attachment.file, {
              signal: controller.signal,
              onProgress: (progress) => {
                setNewConversationAttachments((current) =>
                  current.map((item) => (item.id === attachment.id ? { ...item, progress } : item)),
                );
              },
            });
            if (
              newConversationGenerationRef.current !== generation ||
              removedNewConversationAttachmentIdsRef.current.has(attachment.id)
            ) {
              try {
                await deleteWorkspaceInput(draft.id, uploaded.path);
              } catch (cleanupCause) {
                if (newConversationGenerationRef.current === generation) {
                  removedNewConversationAttachmentIdsRef.current.delete(attachment.id);
                  setNewConversationAttachments((current) => [
                    ...current.filter((item) => item.id !== attachment.id),
                    {
                      ...attachment,
                      status: "uploaded",
                      progress: 100,
                      path: uploaded.path,
                      error: undefined,
                    },
                  ]);
                }
                setError(`文件已上传，但删除失败：${messageOf(cleanupCause)}`);
              }
              continue;
            }
            setNewConversationAttachments((current) =>
              current.map((item) =>
                item.id === attachment.id
                  ? {
                      ...item,
                      status: "uploaded",
                      progress: 100,
                      path: uploaded.path,
                      error: undefined,
                    }
                  : item,
              ),
            );
          } catch (cause) {
            if (removedNewConversationAttachmentIdsRef.current.has(attachment.id)) {
              setNewConversationAttachments((current) =>
                current.filter((item) => item.id !== attachment.id),
              );
            } else {
              setNewConversationAttachments((current) =>
                current.map((item) =>
                  item.id === attachment.id
                    ? { ...item, status: "failed", error: messageOf(cause) }
                    : item,
                ),
              );
            }
          } finally {
            newConversationUploadControllersRef.current.delete(attachment.id);
          }
        }
      })
      .catch((cause: unknown) => setError(messageOf(cause)));
  }

  async function removeNewConversationAttachment(attachment: ComposerAttachment) {
    if (attachment.status === "deleting") return;
    if (attachment.status === "queued" || attachment.status === "uploading") {
      removedNewConversationAttachmentIdsRef.current.add(attachment.id);
      newConversationUploadControllersRef.current.get(attachment.id)?.abort();
      setNewConversationAttachments((current) =>
        current.filter((item) => item.id !== attachment.id),
      );
      return;
    }
    if (attachment.status === "failed" || !attachment.path) {
      removedNewConversationAttachmentIdsRef.current.add(attachment.id);
      setNewConversationAttachments((current) =>
        current.filter((item) => item.id !== attachment.id),
      );
      return;
    }
    const draft = newConversationDraftRef.current;
    if (!draft) return;
    setNewConversationAttachments((current) =>
      current.map((item) => (item.id === attachment.id ? { ...item, status: "deleting" } : item)),
    );
    try {
      await deleteWorkspaceInput(draft.id, attachment.path);
      removedNewConversationAttachmentIdsRef.current.add(attachment.id);
      setNewConversationAttachments((current) =>
        current.filter((item) => item.id !== attachment.id),
      );
    } catch (cause) {
      setNewConversationAttachments((current) =>
        current.map((item) => (item.id === attachment.id ? { ...item, status: "uploaded" } : item)),
      );
      setError(messageOf(cause));
    }
  }

  function retryNewConversationAttachment(attachment: ComposerAttachment) {
    removedNewConversationAttachmentIdsRef.current.delete(attachment.id);
    setNewConversationAttachments((current) =>
      current.map((item) =>
        item.id === attachment.id
          ? { ...item, status: "queued", progress: 0, error: undefined }
          : item,
      ),
    );
    scheduleNewConversationUploads([attachment]);
  }

  async function beginConversation(
    content: string,
    seriesId: string,
    collaborationMode: "default" | "plan",
  ) {
    const series = options?.series.find((item) => item.seriesId === seriesId);
    if (!options || !series) {
      setError("所选模型系列不在当前配置目录中。");
      return;
    }

    setBusy(true);
    setLaunchingGoal(content);
    setError("");
    let created: SessionDetail | null = newConversationDraftRef.current;
    let turnSubmissionAttempted = false;
    try {
      if (newConversationAttachments.some((item) => item.status !== "uploaded")) {
        throw new Error("请等待全部文件上传完成，或删除上传失败的文件。");
      }
      let next: TaskRuntimeState & { session_title?: string; session_title_pending?: boolean };
      if (
        !created &&
        !newConversationDraftPromiseRef.current &&
        newConversationAttachments.length === 0
      ) {
        turnSubmissionAttempted = true;
        created = await createConversation(user.id, content, series.seriesId, collaborationMode);
        next = created.runtime;
      } else {
        created ??= await ensureNewConversationDraft();
        created = await waitForSessionReady(created.id);
        newConversationDraftRef.current = created;
        if (created.series_id !== series.seriesId) {
          created = await updateSessionSeries(created.id, series.seriesId);
          newConversationDraftRef.current = created;
        }
        turnSubmissionAttempted = true;
        next = await createTaskTurn(
          created.id,
          content,
          collaborationMode,
          messageAttachmentsFromComposer(newConversationAttachments),
        );
      }
      const activeSession: SessionDetail = {
        ...created,
        title: next.session_title ?? created.title,
        task_title_assigned:
          next.session_title_pending === undefined
            ? created.task_title_assigned
            : next.session_title_pending !== true,
        runtime_state: next.state,
        runtime: next,
      };
      setSelectedId(created.id);
      setSession(activeSession);
      setNewConversationGoal("");
      setNewConversationAttachments([]);
      newConversationDraftRef.current = null;
      newConversationGenerationRef.current += 1;
      removedNewConversationAttachmentIdsRef.current.clear();
      setSessions((current) => [
        activeSession,
        ...current.filter((item) => item.id !== activeSession.id),
      ]);
      setAdminView(false);
      void refreshSessions();
    } catch (cause) {
      if (created) {
        try {
          await refreshSessions();
          if (turnSubmissionAttempted) {
            const recovered = await getSession(created.id);
            setSelectedId(created.id);
            setSession(recovered);
            setNewConversationGoal("");
            setNewConversationAttachments([]);
            newConversationDraftRef.current = null;
          } else {
            newConversationDraftRef.current = created;
          }
        } catch {
          // 原始错误比恢复失败更有助于用户定位首轮任务问题。
        }
      }
      setError(messageOf(cause));
    } finally {
      setLaunchingGoal(null);
      setBusy(false);
    }
  }

  async function removeSession(item: SessionSummary) {
    if (!window.confirm(`删除会话“${item.title}”及其私有运行数据？`)) return;
    setBusy(true);
    try {
      await deleteSession(item.id);
      if (selectedId === item.id) setSession(null);
      await refreshSessions();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    try {
      await logout();
    } finally {
      onLogout();
    }
  }

  const syncSessionRuntimeState = useCallback(
    (
      sessionId: string,
      runtimeState: TaskRuntimeState["state"],
      lifecycleStatus: SessionSummary["status"],
    ) => {
      setSessions((current) => {
        let changed = false;
        const runStatus = sessionRunStatus(runtimeState);
        const next = current.map((item) => {
          if (
            item.id !== sessionId ||
            (item.runtime_state === runtimeState &&
              item.run_status === runStatus &&
              item.status === lifecycleStatus)
          ) {
            return item;
          }
          changed = true;
          return {
            ...item,
            status: lifecycleStatus,
            runtime_state: runtimeState,
            run_status: runStatus,
          };
        });
        return changed ? next : current;
      });
    },
    [],
  );

  const handleSessionChanged = useCallback(
    (updated: SessionDetail) => {
      setSession(updated);
      void refreshSessions();
    },
    [refreshSessions],
  );

  return (
    <div className="app-shell">
      <WorkspaceSidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
        onToggleMobile={() => setMobileSidebarOpen((current) => !current)}
      >
        {user.role === "user" && (
          <button className="new-task" onClick={openNewConversation} disabled={busy}>
            <Plus size={17} /> <span>新任务</span>
          </button>
        )}
        <nav className="session-list" aria-label="会话">
          {sessions.map((item) => (
            <div
              key={item.id}
              className={`session-row ${selectedId === item.id && !adminView ? "active" : ""}`}
            >
              <button
                className="session-select"
                onClick={() => {
                  setSelectedId(item.id);
                  setAdminView(false);
                  setUsageView(false);
                  setMobileSidebarOpen(false);
                }}
              >
                {isSessionRunning(item) ? (
                  <span
                    aria-label={`${item.title}正在运行`}
                    className="session-running-indicator"
                    role="img"
                  >
                    <LoaderCircle className="spin" size={15} />
                  </span>
                ) : null}
                <ScrollableSessionTitle title={item.title} />
              </button>
              <button
                className="icon-button subtle"
                aria-label={`删除 ${item.title}`}
                onClick={() => void removeSession(item)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          {user.role === "user" && (
            <button
              className={`sidebar-action ${usageView ? "active" : ""}`}
              onClick={() => {
                setUsageView(true);
                setAdminView(false);
                setMobileSidebarOpen(false);
              }}
            >
              <BarChart3 size={16} /> <span>用量与计费</span>
            </button>
          )}
          {user.role === "admin" && (
            <button
              className={`sidebar-action ${adminView ? "active" : ""}`}
              onClick={() => {
                setAdminView(true);
                setUsageView(false);
                setMobileSidebarOpen(false);
              }}
            >
              <ShieldCheck size={16} /> <span>管理中心</span>
            </button>
          )}
          <div className="account-chip">
            <span className="account-email" title={user.email}>
              {user.email}
            </span>
            <div className="account-actions">
              <button
                aria-label="账户设置"
                className="icon-button"
                title="账户设置"
                onClick={() => setAccountSettingsOpen(true)}
              >
                <Settings size={16} />
              </button>
              <button
                aria-label="退出登录"
                className="icon-button"
                title="退出登录"
                onClick={() => void signOut()}
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </WorkspaceSidebar>

      <main className="main-area">
        {error && (
          <div className="top-error">
            <ErrorBanner message={error} onClose={() => setError("")} />
          </div>
        )}
        {adminView ? (
          <AdminCenter />
        ) : usageView ? (
          <UsageCenter />
        ) : launchingGoal ? (
          <TaskLaunchScreen goal={launchingGoal} attachments={newConversationAttachments} />
        ) : session && options ? (
          <TaskWorkspace
            key={session.id}
            identityId={user.id}
            initialSession={session}
            initialDemo={
              initialDemo?.session.id === session.id && readPendingDemo() ? initialDemo : undefined
            }
            options={options}
            guides={guides}
            onSessionChanged={handleSessionChanged}
            onRuntimeStateChanged={syncSessionRuntimeState}
          />
        ) : busy ? (
          <LoadingScreen label="正在准备工作区…" />
        ) : (
          <NewConversation
            options={options}
            busy={busy}
            goal={newConversationGoal}
            attachments={newConversationAttachments}
            onFiles={addNewConversationFiles}
            onRemoveAttachment={(attachment) => {
              void removeNewConversationAttachment(attachment);
            }}
            onRetryAttachment={retryNewConversationAttachment}
            onUploadBlocked={setError}
            onGoalChange={setNewConversationGoal}
            onSubmit={beginConversation}
          />
        )}
      </main>
      {accountSettingsOpen && (
        <AccountSettingsDialog user={user} onClose={() => setAccountSettingsOpen(false)} />
      )}
    </div>
  );
}

function AccountSettingsDialog({ user, onClose }: { user: AuthUser; onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 12) {
      setSuccess("");
      setError("新密码至少需要 12 个字符");
      return;
    }
    if (newPassword !== confirmation) {
      setSuccess("");
      setError("两次输入的新密码不一致");
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await changeOwnPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      setShowPassword(false);
      setSuccess("密码已更新，其他设备上的旧登录状态已失效。");
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !busy) onClose();
      }}
    >
      <section
        aria-labelledby="account-settings-title"
        aria-modal="true"
        className="modal-card account-settings-dialog"
        role="dialog"
      >
        <header className="modal-header">
          <div>
            <p className="eyebrow">ACCOUNT</p>
            <h2 id="account-settings-title">账户设置</h2>
          </div>
          <button
            aria-label="关闭账户设置"
            className="icon-button"
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>
        <dl className="account-summary">
          <dt>邮箱</dt>
          <dd>{user.email}</dd>
          <dt>角色</dt>
          <dd>{user.role === "admin" ? "管理员" : "用户"}</dd>
        </dl>
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <label>
            当前密码
            <input
              aria-label="当前密码"
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              required
              type={showPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>
          <label>
            新密码
            <input
              aria-label="新密码"
              autoComplete="new-password"
              disabled={busy}
              minLength={12}
              maxLength={128}
              required
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <label>
            确认新密码
            <input
              aria-label="确认新密码"
              autoComplete="new-password"
              disabled={busy}
              minLength={12}
              maxLength={128}
              required
              type={showPassword ? "text" : "password"}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          <label className="inline-check">
            <input
              type="checkbox"
              checked={showPassword}
              disabled={busy}
              onChange={(event) => setShowPassword(event.target.checked)}
            />
            显示密码
          </label>
          {error && <ErrorBanner message={error} />}
          {success && (
            <div className="success-banner" role="status">
              {success}
            </div>
          )}
          <div className="modal-actions">
            <button className="secondary" disabled={busy} type="button" onClick={onClose}>
              取消
            </button>
            <button
              className="primary"
              disabled={busy || !currentPassword || !newPassword || !confirmation}
              type="submit"
            >
              {busy ? "正在更新…" : "更新密码"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ScrollableSessionTitle({ title }: { title: string }) {
  function scrollTo(left: number, element: HTMLSpanElement) {
    if (typeof element.scrollTo === "function") {
      element.scrollTo({ left, behavior: "smooth" });
    }
  }

  return (
    <span
      className="session-title-viewport"
      title={title}
      onMouseEnter={(event) => scrollTo(event.currentTarget.scrollWidth, event.currentTarget)}
      onMouseLeave={(event) => scrollTo(0, event.currentTarget)}
    >
      <span className="session-title">{title}</span>
    </span>
  );
}

export function isSessionRunning(session: SessionSummary): boolean {
  if (typeof session.runtime_state === "string") {
    return ACTIVE_STATES.has(session.runtime_state);
  }
  return session.run_status === "running";
}

function sessionRunStatus(state: TaskRuntimeState["state"]): SessionSummary["run_status"] {
  if (state === "idle" || state === "completed" || state === "failed" || state === "cancelled") {
    return state;
  }
  return "running";
}

function TaskWorkspace({
  identityId,
  initialSession,
  initialDemo,
  options,
  guides,
  onSessionChanged,
  onRuntimeStateChanged,
}: {
  identityId: string;
  initialSession: SessionDetail;
  initialDemo?: ContinuedDemo;
  options: RuntimeOptions;
  guides: GuideSummary[];
  onSessionChanged: (session: SessionDetail) => void;
  onRuntimeStateChanged: (
    sessionId: string,
    state: TaskRuntimeState["state"],
    lifecycleStatus: SessionSummary["status"],
  ) => void;
}) {
  const [session, setSession] = useState(initialSession);
  const [runtime, setRuntime] = useState(initialSession.runtime);
  const [currentReport, setCurrentReport] = useState<SessionReportResponse | null>(null);
  const [goal, setGoal] = useState(initialDemo?.question ?? "");
  const [importedContext, setImportedContext] = useState(initialDemo?.importedContext ?? "");
  const historySeed = useRef(initialDemo?.snapshot).current;
  const demoHistory = useDemoHistory(identityId, session.id, historySeed);
  const [demoResultId, setDemoResultId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [workspaceRevision, setWorkspaceRevision] = useState(0);
  const [fileRequest, setFileRequest] = useState<{ path: string } | null>(null);
  const clearFileRequest = useCallback(() => setFileRequest(null), []);
  const [composerMode, setComposerMode] = useState<"default" | "plan">("default");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [rightRailOpen, setRightRailOpen] = useState(() => !compactRailViewport());
  const [rightRailView, setRightRailView] = useState<"workspace" | "result">("workspace");
  const [resultDocumentId, setResultDocumentId] = useState<string | null>(null);
  const eventEndRef = useRef<HTMLDivElement>(null);
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const uploadControllersRef = useRef(new Map<string, AbortController>());
  const removedAttachmentIdsRef = useRef(new Set<string>());
  const approvalSubmissionRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      for (const controller of uploadControllersRef.current.values()) {
        controller.abort();
      }
    },
    [],
  );

  const mergeRuntime = useCallback((next: TaskRuntimeState) => {
    setRuntime((current) => mergeTaskState(current, next));
  }, []);

  useEffect(() => {
    if (runtime.execution_phase !== "report") return;
    let active = true;
    void getSessionReport(session.id)
      .then((report) => {
        if (active) setCurrentReport(report);
      })
      .catch(() => {
        if (active) setCurrentReport(null);
      });
    return () => {
      active = false;
    };
  }, [
    runtime.execution_phase,
    runtime.state,
    runtime.result_document?.report?.attemptRevision,
    session.id,
  ]);

  useEffect(() => {
    onRuntimeStateChanged(session.id, runtime.state, runtime.session_status ?? session.status);
  }, [onRuntimeStateChanged, runtime.session_status, runtime.state, session.id, session.status]);

  useEffect(() => {
    if (!EVENT_SYNC_STATES.has(runtime.state)) return;
    let cancelled = false;
    let timer: number | undefined;
    let source: EventSource | undefined;
    const poll = async () => {
      source?.close();
      timer = undefined;
      try {
        const next = await getTaskEvents(session.id, runtime.events_cursor);
        if (cancelled) return;
        mergeRuntime(next);
        if (EVENT_SYNC_STATES.has(next.state)) {
          timer = window.setTimeout(() => void poll(), 1000);
        }
      } catch (cause) {
        if (cancelled) return;
        setError(messageOf(cause));
        timer = window.setTimeout(() => void poll(), 1000);
      }
    };
    if (typeof globalThis.EventSource === "function") {
      source = new EventSource(taskEventsStreamUrl(session.id, runtime.events_cursor));
      const wakeFromRegisteredEvent = () => {
        if (cancelled) return;
        source?.close();
        if (timer !== undefined) window.clearTimeout(timer);
        timer = window.setTimeout(() => void poll(), 0);
      };
      for (const kind of TaskEventKinds) {
        source.addEventListener(kind, wakeFromRegisteredEvent);
      }
      source.onerror = () => {
        if (cancelled) return;
        source?.close();
        if (timer !== undefined) window.clearTimeout(timer);
        timer = window.setTimeout(() => void poll(), 1000);
      };
      // SSE 通知只是加速；静默丢通知时仍在两秒内核对数据库快照。
      timer = window.setTimeout(() => void poll(), 2000);
    } else {
      timer = window.setTimeout(() => void poll(), 1000);
    }
    return () => {
      cancelled = true;
      source?.close();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [runtime.state, runtime.events_cursor, session.id, mergeRuntime]);

  useEffect(() => {
    if (session.task_title_assigned || runtime.state === "idle") return;
    let cancelled = false;
    let timer: number | undefined;
    const pollTitle = async () => {
      try {
        const refreshed = await getSession(session.id);
        if (cancelled) return;
        if (refreshed.task_title_assigned) {
          setSession(refreshed);
          mergeRuntime(refreshed.runtime);
          onSessionChanged(refreshed);
          return;
        }
      } catch {
        // 标题是非关键元数据，短暂失败时静默重试，不打断任务执行。
      }
      if (!cancelled) {
        timer = window.setTimeout(() => void pollTitle(), 1000);
      }
    };
    timer = window.setTimeout(() => void pollTitle(), 500);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [mergeRuntime, onSessionChanged, runtime.state, session.id, session.task_title_assigned]);

  // 临时状态变化不抢占用户的阅读位置；仅对话内容和交互变化触发滚动。
  const conversationEventCount = runtime.events.filter(
    (event) =>
      event.event_type !== "planning_progress" &&
      event.event_type !== "model_activity" &&
      event.event_type !== "turn_transitioned" &&
      event.event_type !== "result_document_updated" &&
      !hiddenRoutineExecutionProgress(event),
  ).length;
  const conversationState = ACTIVE_STATES.has(runtime.state) ? "active" : runtime.state;
  useEffect(() => {
    if (typeof eventEndRef.current?.scrollIntoView === "function") {
      eventEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [
    conversationEventCount,
    runtime.plan?.revision,
    conversationState,
    demoHistory.snapshot?.caseDigest,
  ]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(COMPACT_RAIL_QUERY);
    let wasCompact = media.matches;
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches && !wasCompact) setRightRailOpen(false);
      wasCompact = event.matches;
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  const selectedSeries = options.series.find((item) => item.seriesId === session.series_id);
  const lifecycleStatus = runtime.session_status ?? session.status;
  const sessionInteractive = lifecycleStatus === "active";
  const isActive = ACTIVE_STATES.has(runtime.state);
  const collaborationModeLocked =
    isActive ||
    runtime.state === "awaiting_plan_start" ||
    runtime.state === "awaiting_action_approval" ||
    runtime.state === "awaiting_user_input";
  const effectiveComposerMode = collaborationModeLocked
    ? (runtime.collaboration_mode ?? composerMode)
    : composerMode;
  const modelSelectionLocked = !sessionInteractive || MODEL_LOCKED_STATES.has(runtime.state);
  const inputMutable = sessionInteractive && INPUT_MUTABLE_STATES.has(runtime.state);
  const uploadDisabled = busy || !inputMutable;
  const uploadDisabledReason = busy
    ? "当前正在处理会话操作，请稍候。"
    : inputUploadDisabledReason(runtime.state, lifecycleStatus);
  const unsettledUploads = attachments.some((item) => item.status !== "uploaded");
  const dropZone = useComposerDropZone({
    disabled: uploadDisabled,
    disabledReason: uploadDisabledReason,
    onBlocked: setError,
    onFiles: addSessionFiles,
  });
  const pendingQuestion = findPendingQuestion(runtime.events, runtime.pending_request_id ?? null);
  const pendingActionApproval =
    runtime.state === "awaiting_action_approval" ? findPendingActionApproval(runtime.events) : null;
  const currentFailure = runtime.state === "failed" ? latestTaskFailure(runtime.events) : null;
  const summaryRecordedInTimeline = Boolean(
    runtime.summary &&
    runtime.events.some((event) => completedTaskSummary(event) === runtime.summary?.trim()),
  );
  const currentResultDocument = runtime.result_document ?? null;
  const selectedResultDocumentId = resultDocumentId ?? currentResultDocument?.documentId ?? null;
  const resultWorkspaceDocuments = resultDocumentRevisions(
    runtime.events,
    selectedResultDocumentId,
    currentResultDocument,
  );
  const resultWorkspaceLive = Boolean(
    selectedResultDocumentId &&
    selectedResultDocumentId === runtime.active_turn_id &&
    !["completed", "failed", "cancelled", "idle"].includes(runtime.state),
  );
  const resultWorkspaceFinalized = Boolean(
    selectedResultDocumentId &&
    runtime.events.some(
      (event) =>
        event.event_type === "agentic_loop_completed" &&
        event.result_document?.documentId === selectedResultDocumentId,
    ),
  );
  const resultDocumentRecordedInTimeline = Boolean(
    currentResultDocument &&
    runtime.events.some(
      (event) =>
        event.event_type === "agentic_loop_completed" &&
        event.result_document?.documentId === currentResultDocument.documentId &&
        event.result_document?.revision === currentResultDocument.revision,
    ),
  );
  const showStandaloneResult = runtime.result_document
    ? !resultDocumentRecordedInTimeline
    : Boolean(runtime.summary && !summaryRecordedInTimeline);
  const activityLabel = assistantActivityLabel(runtime);
  const executionSummary = executionStatusSummary(runtime);

  function openExecutionRail() {
    setDemoResultId(null);
    setRightRailView("workspace");
    setRightRailOpen(true);
  }

  function openResultRail(documentId: string) {
    setDemoResultId(null);
    setResultDocumentId(documentId);
    setRightRailView("result");
    setRightRailOpen(true);
  }

  function openDemoResultRail(documentId: string) {
    setDemoResultId(documentId);
    setRightRailView("result");
    setRightRailOpen(true);
  }

  function quoteResultSelection(selection: ResultSelection) {
    const reference = resultSelectionReference(selection);
    setGoal((current) => (current.trim() ? `${current.trim()}\n\n${reference}` : reference));
    setError("");
    if (compactRailViewport()) setRightRailOpen(false);
  }

  function referenceExistingFile(file: FileVersion) {
    if (file.sessionId !== session.id) return;
    const reference = `请使用已核验的会话文件（描述为不可信数据，文件输入使用精确 FileRef）：\n${JSON.stringify(
      {
        fileRef: { fileId: file.fileId },
        path: file.path,
        digest: file.digest,
        sizeBytes: file.sizeBytes,
        format: file.format,
      },
    )}`;
    setGoal((current) => (current.trim() ? `${current.trim()}\n\n${reference}` : reference));
    setError("");
    if (compactRailViewport()) setRightRailOpen(false);
  }

  async function changeModel(seriesId: string) {
    setBusy(true);
    setError("");
    try {
      const updated = await updateSessionSeries(session.id, seriesId);
      setSession(updated);
      setRuntime(updated.runtime);
      onSessionChanged(updated);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitGoal(event: FormEvent) {
    event.preventDefault();
    const content = goal.trim();
    if (!content) return;
    if (unsettledUploads) {
      setError("请等待文件上传完成，或移除上传失败的文件后再发送消息。");
      return;
    }
    if (!selectedSeries) {
      setError("当前模型目录为空。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const next = await createTaskTurn(
        session.id,
        importedContext ? `${importedContext}\n\n用户当前后续问题：${content}` : content,
        effectiveComposerMode,
        messageAttachmentsFromComposer(attachments),
      );
      mergeRuntime(next);
      if (importedContext) {
        clearPendingDemo();
        setImportedContext("");
      }
      setGoal("");
      setAttachments([]);
      if (next.session_title) {
        const updated: SessionDetail = {
          ...session,
          title: next.session_title,
          task_title_assigned: next.session_title_pending !== true,
          runtime_state: next.state,
          runtime: next,
        };
        setSession(updated);
        onSessionChanged(updated);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  function addSessionFiles(files: File[]) {
    const accepted = validComposerFiles(
      files,
      options.workspace_upload,
      attachments.length,
      attachments.reduce((total, item) => total + item.file.size, 0),
    );
    if (accepted.error) setError(accepted.error);
    if (accepted.files.length === 0) return;
    const items = createComposerAttachments(accepted.files);
    setAttachments((current) => [...current, ...items]);
    scheduleSessionUploads(items);
  }

  function scheduleSessionUploads(items: ComposerAttachment[]) {
    uploadQueueRef.current = uploadQueueRef.current
      .then(async () => {
        for (const attachment of items) {
          if (removedAttachmentIdsRef.current.has(attachment.id)) continue;
          const controller = new AbortController();
          uploadControllersRef.current.set(attachment.id, controller);
          setAttachments((current) =>
            current.map((item) =>
              item.id === attachment.id
                ? { ...item, status: "uploading", progress: 0, error: undefined }
                : item,
            ),
          );
          try {
            const uploaded = await uploadWorkspaceInput(session.id, attachment.file, {
              signal: controller.signal,
              onProgress: (progress) => {
                setAttachments((current) =>
                  current.map((item) => (item.id === attachment.id ? { ...item, progress } : item)),
                );
              },
            });
            if (removedAttachmentIdsRef.current.has(attachment.id)) {
              try {
                await deleteWorkspaceInput(session.id, uploaded.path);
                setWorkspaceRevision((current) => current + 1);
              } catch (cleanupCause) {
                removedAttachmentIdsRef.current.delete(attachment.id);
                setAttachments((current) => [
                  ...current.filter((item) => item.id !== attachment.id),
                  {
                    ...attachment,
                    status: "uploaded",
                    progress: 100,
                    path: uploaded.path,
                    error: undefined,
                  },
                ]);
                setError(`文件已上传，但删除失败：${messageOf(cleanupCause)}`);
              }
              continue;
            }
            setAttachments((current) =>
              current.map((item) =>
                item.id === attachment.id
                  ? {
                      ...item,
                      status: "uploaded",
                      progress: 100,
                      path: uploaded.path,
                      error: undefined,
                    }
                  : item,
              ),
            );
            setWorkspaceRevision((current) => current + 1);
          } catch (cause) {
            if (removedAttachmentIdsRef.current.has(attachment.id)) {
              setAttachments((current) => current.filter((item) => item.id !== attachment.id));
            } else {
              setAttachments((current) =>
                current.map((item) =>
                  item.id === attachment.id
                    ? { ...item, status: "failed", error: messageOf(cause) }
                    : item,
                ),
              );
            }
          } finally {
            uploadControllersRef.current.delete(attachment.id);
          }
        }
      })
      .catch((cause: unknown) => {
        setError(messageOf(cause));
      });
  }

  async function removeSessionAttachment(attachment: ComposerAttachment) {
    if (attachment.status === "deleting") return;
    if (attachment.status === "queued" || attachment.status === "uploading") {
      removedAttachmentIdsRef.current.add(attachment.id);
      uploadControllersRef.current.get(attachment.id)?.abort();
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
      return;
    }
    if (attachment.status === "failed" || !attachment.path) {
      removedAttachmentIdsRef.current.add(attachment.id);
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
      return;
    }
    setAttachments((current) =>
      current.map((item) => (item.id === attachment.id ? { ...item, status: "deleting" } : item)),
    );
    try {
      await deleteWorkspaceInput(session.id, attachment.path);
      removedAttachmentIdsRef.current.add(attachment.id);
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
      setWorkspaceRevision((current) => current + 1);
    } catch (cause) {
      setAttachments((current) =>
        current.map((item) => (item.id === attachment.id ? { ...item, status: "uploaded" } : item)),
      );
      setError(messageOf(cause));
    }
  }

  function retrySessionAttachment(attachment: ComposerAttachment) {
    removedAttachmentIdsRef.current.delete(attachment.id);
    setAttachments((current) =>
      current.map((item) =>
        item.id === attachment.id
          ? { ...item, status: "queued", progress: 0, error: undefined }
          : item,
      ),
    );
    scheduleSessionUploads([attachment]);
  }

  async function handleGuideSelection(value: string) {
    setBusy(true);
    try {
      const next =
        value === "__auto__"
          ? await selectGuide(session.id, "auto")
          : value === "__none__"
            ? await selectGuide(session.id, "none")
            : await selectGuide(session.id, "manual", value);
      setRuntime(next);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function stopActiveTask() {
    setBusy(true);
    setError("");
    try {
      setRuntime(await interruptTask(session.id));
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function resumeStoppedTask() {
    setBusy(true);
    setError("");
    try {
      const instruction = goal.trim();
      setRuntime(await resumeTask(session.id, instruction || null));
      setGoal("");
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  const workspaceView = (
    <div
      className={`task-workspace-layout ${rightRailOpen ? "rail-open" : "rail-closed"}${rightRailOpen && rightRailView === "result" ? " result-open" : ""}`}
    >
      <div
        {...dropZone.dropZoneProps}
        className={`task-shell ${dropZone.dragging ? "dragging-files" : ""}`}
      >
        <TaskWorkspaceHeader
          title={session.title}
          identity={
            <>
              会话 ID <code>{session.id}</code>
            </>
          }
          actions={
            <>
              {!rightRailOpen && (
                <button
                  aria-label="打开任务执行与文件树菜单"
                  className="icon-button framed"
                  onClick={openExecutionRail}
                  title="打开任务执行与文件树"
                >
                  <PanelRightOpen size={17} />
                </button>
              )}
              <button
                aria-label="查看会话用量与工具调用"
                className="icon-button framed"
                onClick={() => setShowUsage(true)}
                title="查看会话用量与工具调用"
                type="button"
              >
                <BarChart3 size={17} />
              </button>
              <button
                className="icon-button framed"
                aria-label="任务设置"
                onClick={() => setShowSettings((value) => !value)}
              >
                <Settings size={17} />
              </button>
            </>
          }
        />

        {!rightRailOpen && runtime.state !== "idle" && (
          <button
            aria-label={`打开任务执行详情：${executionSummary.label}`}
            className={`task-execution-peek ${executionSummary.tone}`}
            onClick={openExecutionRail}
            type="button"
          >
            {executionSummary.active ? (
              <LoaderCircle aria-hidden="true" className="spin" size={15} />
            ) : (
              <Activity aria-hidden="true" size={15} />
            )}
            <span>
              <small>任务执行</small>
              <strong>{executionSummary.label}</strong>
            </span>
            <ChevronRight aria-hidden="true" size={15} />
          </button>
        )}

        {showSettings && (
          <section className="settings-panel">
            <div className="setting-grid">
              <label>
                Guide 策略
                <select
                  aria-label="Guide 策略"
                  disabled={
                    !sessionInteractive || !GUIDE_EDITABLE_STATES.has(runtime.state) || busy
                  }
                  value={
                    runtime.guide_preference === "none"
                      ? "__none__"
                      : runtime.guide_preference === "manual"
                        ? (runtime.manual_guide_id ?? "__auto__")
                        : "__auto__"
                  }
                  onChange={(event) => void handleGuideSelection(event.target.value)}
                >
                  <option value="__auto__">根据任务自动匹配</option>
                  <option value="__none__">不引入 Guide</option>
                  {guides.map((guide) => (
                    <option key={guide.definition_id} value={guide.definition_id}>
                      {guide.display_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        )}

        {showUsage && (
          <SessionUsageDialog
            events={runtime.events}
            sessionId={session.id}
            onClose={() => setShowUsage(false)}
          />
        )}

        <div className="conversation">
          {demoHistory.loading && <p role="status">正在恢复研究记录…</p>}
          {demoHistory.error && <p role="alert">{demoHistory.error}</p>}
          {demoHistory.snapshot && (
            <DemoHistory
              snapshot={demoHistory.snapshot}
              onOpenResult={openDemoResultRail}
              inlineResultMedia={!(rightRailOpen && rightRailView === "result")}
            />
          )}
          {runtime.events.length === 0 &&
            !demoHistory.snapshot &&
            !demoHistory.loading &&
            !demoHistory.error && (
              <section className="welcome-card">
                <Bot size={28} />
                <h2>我们先从哪里开始呢？</h2>
                <p>
                  直接告诉我你的研究目标、已有材料或想验证的问题。
                  我会直接推进任务，只在需要补充信息或逐次确认动作时打断你。
                </p>
              </section>
            )}

          {runtime.guide_match && <GuideCard guide={runtime.guide_match} />}
          <EventTimeline
            events={runtime.events}
            featuredFailureEventId={currentFailure?.event_id ?? null}
            inlineResultMedia={!(rightRailOpen && rightRailView === "result")}
            onOpenResult={openResultRail}
            onUseResultSelection={quoteResultSelection}
            pendingRequestId={runtime.pending_request_id ?? null}
            sessionId={session.id}
          />

          <AssistantActivity label={activityLabel} />

          {runtime.plan && runtime.state === "awaiting_plan_start" && (
            <section
              aria-label={runtime.state === "awaiting_plan_start" ? "开始执行计划" : "执行计划记录"}
              className="conversation-interaction"
            >
              <PlanCard
                key={`${runtime.plan.execution_plan_id}:${runtime.plan.plan_hash}:${runtime.plan.revision}`}
                plan={runtime.plan}
                runtimeState={runtime.state}
                busy={busy}
                onStart={async () => {
                  setBusy(true);
                  try {
                    setRuntime(
                      await startPlan(
                        session.id,
                        runtime.plan!.execution_plan_id,
                        runtime.plan!.plan_hash,
                        runtime.plan!.revision,
                      ),
                    );
                  } catch (cause) {
                    setError(messageOf(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
                onDiscard={async () => {
                  setBusy(true);
                  try {
                    setRuntime(
                      await discardPlan(
                        session.id,
                        runtime.plan!.execution_plan_id,
                        runtime.plan!.plan_hash,
                        runtime.plan!.revision,
                      ),
                    );
                  } catch (cause) {
                    setError(messageOf(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </section>
          )}

          {runtime.state === "awaiting_plan_start" && !runtime.plan && (
            <section aria-label="开始执行计划" className="conversation-interaction">
              <PlanInteractionRecovery
                busy={busy}
                onReload={async () => {
                  setBusy(true);
                  try {
                    const refreshed = await getSession(session.id);
                    setSession(refreshed);
                    setRuntime(refreshed.runtime);
                    onSessionChanged(refreshed);
                  } catch (cause) {
                    setError(messageOf(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </section>
          )}

          {runtime.state === "awaiting_action_approval" && pendingActionApproval && (
            <section aria-label="确认动作" className="conversation-interaction">
              <ActionApprovalCard
                approval={pendingActionApproval}
                busy={busy}
                onDecision={async (decision) => {
                  const submissionKey = actionApprovalKey(pendingActionApproval);
                  if (approvalSubmissionRef.current !== null) return;
                  approvalSubmissionRef.current = submissionKey;
                  setBusy(true);
                  try {
                    setRuntime(
                      await decideAction(
                        session.id,
                        pendingActionApproval.actionId,
                        pendingActionApproval.requestDigest,
                        decision,
                      ),
                    );
                  } catch (cause) {
                    setError(messageOf(cause));
                  } finally {
                    if (approvalSubmissionRef.current === submissionKey) {
                      approvalSubmissionRef.current = null;
                    }
                    setBusy(false);
                  }
                }}
              />
            </section>
          )}

          {runtime.state === "awaiting_action_approval" && !pendingActionApproval && (
            <ActionApprovalSyncStatus />
          )}

          {runtime.state === "awaiting_user_input" && (
            <section aria-label="需要补充信息" className="conversation-interaction">
              <QuestionCard
                key={runtime.pending_request_id ?? "pending-question"}
                event={pendingQuestion}
                busy={busy}
                onSubmit={async (answers) => {
                  if (!runtime.active_turn_id || !runtime.pending_request_id) {
                    setError("当前问题缺少有效请求标识，请重新加载会话。");
                    return;
                  }
                  setBusy(true);
                  try {
                    setRuntime(
                      await answerTaskTurn(
                        session.id,
                        runtime.active_turn_id,
                        runtime.pending_request_id,
                        answers,
                      ),
                    );
                  } catch (cause) {
                    setError(messageOf(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </section>
          )}

          {(runtime.summary || runtime.result_document) && showStandaloneResult && (
            <TaskResultCard
              document={runtime.result_document ?? null}
              events={runtime.events}
              inlineFeatured={!(rightRailOpen && rightRailView === "result")}
              onOpenResult={openResultRail}
              onUseSelection={quoteResultSelection}
              sessionId={session.id}
              summary={runtime.summary ?? "任务已完成。"}
            />
          )}
          {runtime.artifacts &&
            runtime.artifacts.length > 0 &&
            !resultDocumentRecordedInTimeline &&
            !runtime.result_document && (
              <section className="artifact-list">
                <h3>结果文件</h3>
                {runtime.artifacts.map((path) => (
                  <SessionFileLink key={path} path={path} sessionId={session.id} />
                ))}
              </section>
            )}
          {currentFailure && <TaskFailureCard event={currentFailure} />}
          <div ref={eventEndRef} />
        </div>

        {error && (
          <div className="composer-error">
            <ErrorBanner message={error} onClose={() => setError("")} />
          </div>
        )}
        <form className="composer" onSubmit={(event) => void submitGoal(event)}>
          <CollaborationModeMenu
            mode={effectiveComposerMode}
            disabled={busy || !sessionInteractive || collaborationModeLocked}
            onChange={setComposerMode}
          />
          <ComposerUploadButton
            disabled={uploadDisabled}
            disabledReason={uploadDisabledReason}
            onFiles={addSessionFiles}
          />
          <AutoGrowTextarea
            aria-label="任务目标"
            value={goal}
            maxRows={8}
            disabled={
              busy ||
              !sessionInteractive ||
              runtime.state === "awaiting_user_input" ||
              runtime.state === "awaiting_action_approval" ||
              runtime.state === "stopping" ||
              runtime.state === "stop_failed"
            }
            onChange={(event) => setGoal(event.target.value)}
            placeholder={
              runtime.state === "awaiting_plan_start"
                ? "可以询问计划细节，或告诉我需要怎样修改…"
                : runtime.state === "awaiting_action_approval"
                  ? "请先处理上方待审批动作"
                  : isActive
                    ? "任务进行中，可以继续提问或调整方向…"
                    : runtime.state === "awaiting_user_input"
                      ? "请先回答上方问题"
                      : "例如：分析这条 RNA 序列的二级结构、能量与局部可及性…"
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <ComposerAttachmentList
            items={attachments}
            onRemove={(attachment) => void removeSessionAttachment(attachment)}
            onRetry={retrySessionAttachment}
            removeDisabled={uploadDisabled}
            removeDisabledReason={uploadDisabledReason}
          />
          <ConversationModelSelect
            options={options}
            seriesId={selectedSeries?.seriesId ?? null}
            menuDirection="up"
            disabled={modelSelectionLocked || busy}
            onChange={(seriesId) => void changeModel(seriesId)}
          />
          {sessionInteractive && isActive && runtime.state !== "stopping" && (
            <button
              aria-label="停止任务"
              className="primary send stop"
              disabled={busy}
              type="button"
              onClick={() => void stopActiveTask()}
            >
              <Square fill="currentColor" size={11} />
            </button>
          )}
          {runtime.state === "stopping" && (
            <button aria-label="正在停止任务" className="primary send stop" disabled type="button">
              <RefreshCw className="spin" size={15} />
            </button>
          )}
          {sessionInteractive && runtime.state === "stop_failed" && (
            <button
              aria-label="重试停止"
              className="primary send stop"
              disabled={busy}
              type="button"
              onClick={() => void stopActiveTask()}
            >
              <Square fill="currentColor" size={11} />
            </button>
          )}
          {sessionInteractive && RESUMABLE_STATES.has(runtime.state) && (
            <button
              aria-label="继续任务"
              className="primary send resume"
              disabled={busy || unsettledUploads}
              type="button"
              onClick={() => void resumeStoppedTask()}
            >
              <RefreshCw size={15} />
            </button>
          )}
          <button
            aria-label="发送消息"
            className="primary send"
            type="submit"
            disabled={
              busy ||
              !sessionInteractive ||
              !goal.trim() ||
              unsettledUploads ||
              runtime.state === "awaiting_user_input" ||
              runtime.state === "awaiting_action_approval" ||
              runtime.state === "stopping" ||
              runtime.state === "stop_failed"
            }
          >
            {busy ? <RefreshCw className="spin" size={17} /> : <ArrowUp size={18} />}
          </button>
        </form>
        <FileDropOverlay active={dropZone.dragging} />
      </div>
      {rightRailOpen &&
        (rightRailView === "result" && demoResultId && demoHistory.snapshot ? (
          <DemoHistoryScope snapshot={demoHistory.snapshot}>
            <ResultWorkspaceRail
              key={demoResultId}
              documents={demoHistory.snapshot.case.stages
                .filter((stage) => stage.document.documentId === demoResultId)
                .map((stage) => stage.document)}
              finalized
              live={false}
              onClose={() => setRightRailOpen(false)}
              onOpenWorkspace={openExecutionRail}
              onUseSelection={quoteResultSelection}
              sessionId="public-demo"
              publicDemo
            />
          </DemoHistoryScope>
        ) : rightRailView === "result" && resultWorkspaceDocuments.length > 0 ? (
          <ResultWorkspaceRail
            documents={resultWorkspaceDocuments}
            finalized={resultWorkspaceFinalized}
            key={selectedResultDocumentId ?? "result"}
            live={resultWorkspaceLive}
            onClose={() => setRightRailOpen(false)}
            onOpenWorkspace={openExecutionRail}
            onUseSelection={quoteResultSelection}
            sessionId={session.id}
          />
        ) : (
          <WorkspaceRightRail
            sessionId={session.id}
            runtime={runtime}
            workspaceRevision={workspaceRevision}
            fileRequest={fileRequest}
            onFileRequestHandled={clearFileRequest}
            onReferenceFile={referenceExistingFile}
            referenceDisabledReason={uploadDisabled ? uploadDisabledReason : undefined}
            onClose={() => setRightRailOpen(false)}
          />
        ))}
    </div>
  );

  return (
    <SessionFileReferencesContext.Provider
      value={{
        artifacts: [
          runtime.result_document,
          ...runtime.events.map((event) => event.result_document),
        ].flatMap(
          (document) =>
            document?.blocks.flatMap((block) => (block.kind === "artifacts" ? block.items : [])) ??
            [],
        ),
        openFile: (path) => {
          setFileRequest({ path });
          openExecutionRail();
        },
      }}
    >
      <ReportActionsContext.Provider
        value={{
          currentReportId:
            currentReport?.reportId ?? runtime.result_document?.report?.reportId ?? null,
          current: currentReport,
          busy: busy || ACTIVE_STATES.has(runtime.state),
          retry: async ({ revision, evidenceDigest }) => {
            setBusy(true);
            try {
              mergeRuntime(await retrySessionReport(session.id, revision, evidenceDigest));
            } finally {
              setBusy(false);
            }
          },
        }}
      >
        <MissingReportNotice />
        {workspaceView}
      </ReportActionsContext.Provider>
    </SessionFileReferencesContext.Provider>
  );
}

function EventTimeline({
  events,
  featuredFailureEventId,
  inlineResultMedia,
  onOpenResult,
  onUseResultSelection,
  pendingRequestId,
  sessionId,
}: {
  events: TaskEvent[];
  featuredFailureEventId: string | null;
  inlineResultMedia: boolean;
  onOpenResult: (documentId: string) => void;
  onUseResultSelection: (selection: ResultSelection) => void;
  pendingRequestId: string | null;
  sessionId: string;
}) {
  const answeredQuestions = new Map<string, TaskEvent>();
  const answerEventIds = new Set<string>();
  const actionApprovalRequests = new Map<string, PendingActionApproval>();
  const actionApprovalDecisions = new Map<string, TaskEvent>();
  events.forEach((event, index) => {
    if (event.event_type === "action_approval_requested") {
      const approval = actionApprovalFromEvent(event);
      if (approval) actionApprovalRequests.set(actionApprovalKey(approval), approval);
    }
    if (event.event_type === "action_approved" || event.event_type === "action_denied") {
      const key = actionApprovalEventKey(event);
      if (key) actionApprovalDecisions.set(key, event);
    }
    if (event.event_type !== "request_user_input") return;
    const requestId = event.payload?.request_id;
    if (typeof requestId === "string" && requestId === pendingRequestId) return;
    const answer = events[index + 1];
    if (
      answer &&
      (answer.event_type === "user_message" || answer.role === "user") &&
      answer.content
    ) {
      answeredQuestions.set(event.event_id, answer);
      answerEventIds.add(answer.event_id);
    }
  });
  const resolvedActionDecisionIds = new Set(
    [...actionApprovalDecisions.entries()]
      .filter(([key]) => actionApprovalRequests.has(key))
      .map(([, event]) => event.event_id),
  );
  const inlineResultEventId = [...events]
    .reverse()
    .find(
      (event) => event.event_type === "agentic_loop_completed" && event.result_document,
    )?.event_id;
  const latestReportTurn = [...events]
    .reverse()
    .find((event) => event.event_type === "report_delivery_started")?.turn_id;

  return (
    <div className="timeline">
      {events.map((event) => {
        if (event.event_id === featuredFailureEventId) return null;
        if (event.event_type === "result_document_updated") return null;
        if (
          event.result_document?.report?.reportId &&
          latestReportTurn &&
          event.turn_id !== latestReportTurn
        )
          return null;
        if (event.event_type === "planning_progress" || event.event_type === "model_activity")
          return null;
        if (hiddenRoutineExecutionProgress(event)) return null;
        if (isPlanSnapshotEvent(event)) return null;
        if (event.event_type === "guide_introduced" || answerEventIds.has(event.event_id)) {
          return null;
        }
        if (event.event_type === "request_user_input") {
          const answer = answeredQuestions.get(event.event_id);
          return answer ? (
            <QuestionDecisionRecord
              key={event.event_id}
              event={event}
              answer={answer.content ?? ""}
            />
          ) : null;
        }
        if (event.event_type === "action_approval_requested") {
          const approval = actionApprovalFromEvent(event);
          const decision = approval
            ? actionApprovalDecisions.get(actionApprovalKey(approval))
            : undefined;
          return approval && decision ? (
            <ActionApprovalDecisionRecord
              key={event.event_id}
              approval={approval}
              decision={decision}
            />
          ) : null;
        }
        if (resolvedActionDecisionIds.has(event.event_id)) return null;
        const completedSummary = completedTaskSummary(event);
        if (completedSummary) {
          return (
            <TaskResultCard
              document={event.result_document ?? null}
              events={events}
              inlineFeatured={inlineResultMedia && event.event_id === inlineResultEventId}
              key={event.event_id}
              onOpenResult={onOpenResult}
              onUseSelection={onUseResultSelection}
              sessionId={sessionId}
              summary={completedSummary}
            />
          );
        }
        const userMessage = event.event_type === "user_message" || event.role === "user";
        const text = event.content ?? event.message;
        if (!text) return null;
        const attachments = userMessage ? messageAttachmentsFromEvent(event) : [];
        return (
          <ChatMessage
            key={event.event_id}
            role={userMessage ? "user" : "system"}
            label={userMessage ? "你" : eventLabel(event.event_type, event.payload)}
            time={formatTime(event.timestamp)}
            attachments={attachments}
          >
            <SessionMarkdown sessionId={sessionId}>{text}</SessionMarkdown>
          </ChatMessage>
        );
      })}
    </div>
  );
}

function completedTaskSummary(event: TaskEvent): string | null {
  if (event.event_type !== "agentic_loop_completed") return null;
  const summary = event.payload?.summary;
  return typeof summary === "string" && summary.trim() ? summary.trim() : null;
}

function messageAttachmentsFromComposer(attachments: ComposerAttachment[]): MessageAttachment[] {
  return attachments.flatMap((attachment) =>
    attachment.status === "uploaded" && attachment.path
      ? [
          {
            path: attachment.path,
            name: attachment.file.name,
            size: attachment.file.size,
          },
        ]
      : [],
  );
}

function messageAttachmentsFromEvent(event: TaskEvent): MessageAttachment[] {
  const rawAttachments = event.payload?.attachments;
  if (!Array.isArray(rawAttachments)) return [];
  return rawAttachments.flatMap((item) => {
    const candidate = item as Record<string, unknown> | null;
    if (
      !candidate ||
      typeof candidate !== "object" ||
      typeof candidate.path !== "string" ||
      typeof candidate.name !== "string" ||
      typeof candidate.size !== "number"
    ) {
      return [];
    }
    return [
      {
        path: candidate.path,
        name: candidate.name,
        size: candidate.size,
      },
    ];
  });
}

function TaskFailureCard({ event }: { event: TaskEvent }) {
  const code = event.payload?.failure_code;
  const stage = event.payload?.failure_stage;
  return (
    <section aria-label="任务失败详情" className="task-failure-card">
      <div className="task-failure-heading">
        <span aria-hidden="true">
          <X size={16} strokeWidth={2.5} />
        </span>
        <div>
          <p className="eyebrow">任务执行失败</p>
          <h2>任务失败</h2>
        </div>
      </div>
      <div className="task-failure-detail">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{taskFailureMessage(event)}</ReactMarkdown>
        <dl aria-label="失败诊断信息" className="task-failure-diagnostics">
          {typeof code === "string" && code.trim() && (
            <>
              <dt>错误码</dt>
              <dd>
                <code>{code}</code>
              </dd>
            </>
          )}
          {typeof stage === "string" && stage.trim() && (
            <>
              <dt>失败阶段</dt>
              <dd>
                {taskFailureStageLabel(stage)} <code>{stage}</code>
              </dd>
            </>
          )}
          <dt>事件 ID</dt>
          <dd>
            <code>{event.event_id}</code>
          </dd>
        </dl>
      </div>
    </section>
  );
}

function QuestionDecisionRecord({ event, answer }: { event: TaskEvent; answer: string }) {
  const questions = normaliseQuestions(event);
  return (
    <section className="question-card decision-record" aria-label="已回答的问题">
      <p className="eyebrow">已在会话中回答</p>
      <div className="decision-question-list">
        {questions.map((question, index) => (
          <p key={question.id}>
            {questions.length > 1 && <span>{index + 1}</span>}
            {question.prompt}
          </p>
        ))}
      </div>
      <div className="decision-answer">
        <small>你的选择</small>
        <strong>{answer}</strong>
      </div>
    </section>
  );
}

function GuideCard({ guide }: { guide: NonNullable<TaskRuntimeState["guide_match"]> }) {
  return (
    <section className="guide-card">
      <div className="guide-icon">
        <Bot size={18} />
      </div>
      <div>
        <div className="guide-heading">
          <strong>已引入 Guide：{guide.display_name}</strong>
          <code>{guide.content_sha256.slice(0, 10)}</code>
        </div>
        <p>{guide.description}</p>
        <small>{guide.reason} Guide 仅提供建议，不限制编排器选择、重排或重复工具。</small>
      </div>
    </section>
  );
}

interface PendingActionApproval {
  actionId: string;
  requestDigest: string;
  kind: string;
  summary: string;
  reason: string;
  details: Record<string, unknown>;
  requestedCharge: Record<string, unknown>;
}

function ActionApprovalDecisionRecord({
  approval,
  decision,
}: {
  approval: PendingActionApproval;
  decision: TaskEvent;
}) {
  const approved = decision.event_type === "action_approved";
  const approvedForSession = approved && decision.payload?.decision === "allow_session";
  const approvalStatus = approvedForSession ? "本会话均允许" : "已允许本次";
  const approvalAnswer = approvedForSession ? "此会话中均允许此操作" : "仅允许本次动作";
  return (
    <section
      aria-label="已处理动作审批"
      className={`action-approval-card decision-record ${approved ? "approved" : "denied"}`}
    >
      <div className="action-approval-record-heading">
        <p className="eyebrow">需确认动作 · 会话记录</p>
        <span className={`action-approval-status ${approved ? "approved" : "denied"}`}>
          {approved ? approvalStatus : "已拒绝并停止"}
        </span>
      </div>
      <h2>{approval.summary}</h2>
      <ActionApprovalDetails approval={approval} collapsed />
      <div className={`decision-answer ${approved ? "" : "denied"}`}>
        <small>你的决定 · {formatTime(decision.timestamp)}</small>
        <strong>{approved ? approvalAnswer : "拒绝该动作并停止任务"}</strong>
      </div>
    </section>
  );
}

function ActionApprovalCard({
  approval,
  busy,
  onDecision,
}: {
  approval: PendingActionApproval;
  busy: boolean;
  onDecision: (decision: ActionApprovalDecision) => Promise<void>;
}) {
  return (
    <section className="action-approval-card interaction-card">
      <p className="eyebrow">需确认动作 · 等待逐次确认</p>
      <h2>{approval.summary}</h2>
      <ActionApprovalDetails approval={approval} />
      <p className="session-approval-hint">
        选择“此会话中均允许此操作”后，相同工具、相同任务目标的同类调用可继续执行，输入参数可能不同。每次仍需通过参数和预算检查；工具定义或任务目标变化后需重新确认。
      </p>
      <div className="plan-actions">
        <button
          className="secondary"
          disabled={busy}
          type="button"
          onClick={() => void onDecision("deny")}
        >
          拒绝并停止
        </button>
        <button
          className="secondary"
          disabled={busy}
          type="button"
          onClick={() => void onDecision("allow_session")}
        >
          此会话中均允许此操作
        </button>
        <button
          className="primary"
          disabled={busy}
          type="button"
          onClick={() => void onDecision("allow_once")}
        >
          仅允许本次
        </button>
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  runtimeState,
  busy,
  onStart,
  onDiscard,
}: {
  plan: NonNullable<TaskRuntimeState["plan"]>;
  runtimeState: TaskRuntimeState["state"];
  busy: boolean;
  onStart?: () => Promise<void>;
  onDiscard?: () => Promise<void>;
}) {
  const awaitingPlanStart = runtimeState === "awaiting_plan_start";
  const status = planRecordStatus(runtimeState);
  return (
    <TaskPlanCardView
      objective={plan.objective}
      preview={plan.preview}
      steps={plan.operations.map((step) => ({
        id: step.operation_id,
        kind: step.kind,
        description: step.description,
        acceptanceCriteria: step.acceptance_criteria,
        toolLabel: step.tool ? `${step.tool.server} · ${step.tool.function}` : undefined,
      }))}
      assumptions={plan.assumptions}
      checkpoints={plan.human_checkpoints}
      awaitingStart={awaitingPlanStart}
      status={status}
      busy={busy}
      onStart={onStart}
      onDiscard={onDiscard}
      boundary={
        <details>
          <summary>预算与数据披露边界</summary>
          <pre>
            {JSON.stringify(
              {
                estimated_cost: plan.estimated_cost,
                data_disclosure: plan.data_disclosure,
                risk_flags: plan.risk_flags,
                repair_policy: plan.repair_policy,
                loop_policy: plan.loop_policy,
              },
              null,
              2,
            )}
          </pre>
        </details>
      }
    />
  );
}

function planRecordStatus(state: TaskRuntimeState["state"]) {
  if (state === "awaiting_plan_start") return { label: "等待开始", tone: "pending" };
  if (state === "executing" || state === "running") {
    return { label: "执行中", tone: "running" };
  }
  if (state === "completed") return { label: "执行完成", tone: "success" };
  if (state === "failed") return { label: "执行失败", tone: "failed" };
  if (state === "stop_failed") return { label: "停止失败", tone: "failed" };
  if (state === "stopping") return { label: "正在停止", tone: "active" };
  if (state === "cancelled") return { label: "已停止", tone: "muted" };
  return { label: "计划记录", tone: "success" };
}

function QuestionCard({
  event,
  busy,
  onSubmit,
}: {
  event: TaskEvent | null;
  busy: boolean;
  onSubmit: (answers: Record<string, string>) => Promise<void>;
}) {
  const questions = normaliseQuestions(event);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const completed = questions.every((question) => Boolean(answers[question.id]?.trim()));

  return (
    <form
      className="question-card interaction-card"
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        if (completed)
          void onSubmit(
            Object.fromEntries(
              questions.map((question) => [question.id, answers[question.id]?.trim() ?? ""]),
            ),
          );
      }}
    >
      <p className="eyebrow">需要你的判断</p>
      <h2>
        {questions.length > 1 ? `请回答以下 ${questions.length} 个问题` : questions[0].prompt}
      </h2>
      <p className="interaction-hint">回答会加入当前会话上下文，并用于继续规划或执行。</p>
      <div className="question-list">
        {questions.map((question, index) => (
          <fieldset key={question.id} className="question-item">
            {questions.length > 1 && (
              <legend>
                {index + 1}. {question.prompt}
              </legend>
            )}
            {question.options.length > 0 && (
              <div className="question-options">
                {question.options.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={`option-button ${answers[question.id] === option.label ? "selected" : ""}`}
                    disabled={busy}
                    onClick={() =>
                      setAnswers((current) => ({
                        ...current,
                        [question.id]: option.label,
                      }))
                    }
                  >
                    <strong>{option.label}</strong>
                    {option.description && <span>{option.description}</span>}
                  </button>
                ))}
              </div>
            )}
            <textarea
              aria-label={question.prompt}
              rows={3}
              placeholder={question.options.length > 0 ? "也可以输入自定义回答" : "请输入回答"}
              value={answers[question.id] ?? ""}
              disabled={busy}
              onChange={(current) =>
                setAnswers((existing) => ({
                  ...existing,
                  [question.id]: current.target.value,
                }))
              }
            />
          </fieldset>
        ))}
      </div>
      <div className="question-actions">
        <button className="primary" type="submit" disabled={busy || !completed}>
          提交回答并继续
        </button>
      </div>
    </form>
  );
}

function PlanInteractionRecovery({
  busy,
  onReload,
}: {
  busy: boolean;
  onReload: () => Promise<void>;
}) {
  return (
    <section className="interaction-card recovery-card">
      <p className="eyebrow">等待你的操作</p>
      <h2>正在恢复待确认计划</h2>
      <p>会话状态显示有待办交互，但详情尚未加载完整。请重新加载，系统不会自动执行。</p>
      <button className="primary" disabled={busy} onClick={() => void onReload()}>
        {busy ? "正在加载…" : "重新加载计划"}
      </button>
    </section>
  );
}

function ActionApprovalSyncStatus() {
  return <AssistantActivity label="正在同步确认结果" />;
}

function AdminCenter() {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [guides, setGuides] = useState<AdminGuidesResponse | null>(null);
  const [usage, setUsage] = useState<UsageOverview | null>(null);
  const [mcpUsage, setMcpUsage] = useState<McpRawUsage | null>(null);
  const [tab, setTab] = useState<"guides" | "users" | "usage" | "terminations">("guides");
  const [error, setError] = useState("");
  const [reviewingGuideId, setReviewingGuideId] = useState<string | null>(null);
  const [activatingUserId, setActivatingUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState("");
  const [passwordTarget, setPasswordTarget] = useState<AuthUser | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [accountRows, guideRows, usageRows, mcpRows] = await Promise.all([
        listUsers(),
        listAdminGuides(),
        getAdminUsage(),
        getMcpRawUsage(),
      ]);
      setUsers(accountRows);
      setGuides(guideRows);
      setUsage(usageRows);
      setMcpUsage(mcpRows);
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const removeUser = useCallback(
    async (account: AuthUser) => {
      if (
        !window.confirm(
          `删除账号 ${account.email} 及其全部会话？该操作会启动统一删除流程，无法通过界面撤销。`,
        )
      ) {
        return;
      }
      setDeletingUserId(account.id);
      setError("");
      try {
        await deleteUser(account.id);
        await reload();
      } catch (cause) {
        setError(messageOf(cause));
      } finally {
        setDeletingUserId("");
      }
    },
    [reload],
  );

  return (
    <div className="admin-shell">
      {tab === "terminations" && <TerminationAdmin />}
      <header className="admin-header">
        <div>
          <p className="eyebrow">ADMINISTRATION</p>
          <h1>管理中心</h1>
        </div>
        <div className="tabs">
          <button
            className={tab === "terminations" ? "active" : ""}
            onClick={() => setTab("terminations")}
          >
            工具终止
          </button>
          <button className={tab === "guides" ? "active" : ""} onClick={() => setTab("guides")}>
            <Bot size={16} /> Guides
          </button>
          <button className={tab === "users" ? "active" : ""} onClick={() => setTab("users")}>
            <Users size={16} /> 用户
          </button>
          <button className={tab === "usage" ? "active" : ""} onClick={() => setTab("usage")}>
            <BarChart3 size={16} /> 用量
          </button>
        </div>
      </header>
      {error && <ErrorBanner message={error} onClose={() => setError("")} />}
      {tab === "guides" ? (
        <section>
          <div className="admin-note">
            <ShieldCheck size={18} />
            <div>
              <strong>Guide 内容由仓库 skill 管理</strong>
              <p>
                管理员可在此审阅已登记的真实验收证据并明确批准。创建、内容修改与 canary 验收使用{" "}
                <code> author-guided-task</code>，稳定 ID 可原地更新。
              </p>
            </div>
          </div>
          <div className="guide-grid">
            {loading && !guides ? (
              <LoadingScreen label="正在加载 Guide…" />
            ) : guides?.guides.length === 0 ? (
              <p className="muted">当前没有可管理的 Guide。</p>
            ) : (
              guides?.guides.map((guide) => (
                <article key={guide.definition_id} className="admin-guide-card">
                  <div className="guide-heading">
                    <h2>{guide.display_name}</h2>
                    <StatusPill value={guide.effective_status} />
                  </div>
                  <p>{guide.description}</p>
                  <dl>
                    <dt>ID</dt>
                    <dd>
                      <code>{guide.definition_id}</code>
                    </dd>
                    <dt>内容摘要</dt>
                    <dd>
                      <code>{guide.content_sha256}</code>
                    </dd>
                    <dt>声明状态</dt>
                    <dd>{guideStatusLabel(guide.declared_status)}</dd>
                  </dl>
                  <div className="guide-actions">
                    {guide.review.canary ? (
                      <button onClick={() => setReviewingGuideId(guide.definition_id)}>
                        {guide.effective_status === "validated"
                          ? "审阅并批准当前证据"
                          : "查看验收证据"}
                      </button>
                    ) : (
                      <span className="muted">尚未登记真实验收结果</span>
                    )}
                    {guide.review.canary && !guide.review.canaryCurrent && (
                      <p className="muted">当前验收未通过或绑定已失效，需要重新验收。</p>
                    )}
                  </div>
                  <details>
                    <summary>完整定义与验证证据</summary>
                    <pre>{JSON.stringify(guide, null, 2)}</pre>
                  </details>
                </article>
              ))
            )}
          </div>
        </section>
      ) : tab === "users" ? (
        <section className="user-admin">
          <div className="admin-note">
            <ShieldCheck size={18} />
            <div>
              <strong>用户自主注册，管理员手动激活</strong>
              <p>
                当前有 {users.filter((account) => account.status === "pending").length}{" "}
                个账户待激活。请核对注册资料后激活，用户即可使用自己设置的密码登录。
              </p>
              <button disabled={loading} onClick={() => void reload()}>
                刷新用户列表
              </button>
            </div>
          </div>
          <div className="user-table">
            {loading && users.length === 0 ? (
              <LoadingScreen label="正在加载用户…" />
            ) : users.length === 0 ? (
              <p className="muted">当前没有用户账号。</p>
            ) : (
              [...users]
                .sort(
                  (left, right) =>
                    Number(right.status === "pending") - Number(left.status === "pending"),
                )
                .map((account) => (
                  <div key={account.id} className="user-row">
                    <div>
                      <strong>{account.name ?? "未填写姓名"}</strong>
                      <span>{account.email}</span>
                      <small>
                        {account.institution ?? "未填写机构"} · {userGenderLabel(account.gender)}
                      </small>
                      <small>
                        注册时间：
                        {account.created_at
                          ? new Date(account.created_at).toLocaleString("zh-CN")
                          : "未知"}
                      </small>
                      <small>
                        {userRoleLabel(account.role)} · {userStatusLabel(account.status)}
                      </small>
                    </div>
                    {account.status === "pending" ? (
                      <button
                        className="primary"
                        disabled={Boolean(activatingUserId) || Boolean(deletingUserId)}
                        onClick={() => {
                          void (async () => {
                            setActivatingUserId(account.id);
                            setError("");
                            try {
                              await activateUser(account.id);
                              await reload();
                            } catch (cause) {
                              setError(messageOf(cause));
                            } finally {
                              setActivatingUserId("");
                            }
                          })();
                        }}
                      >
                        {activatingUserId === account.id ? "正在激活…" : "激活账户"}
                      </button>
                    ) : (
                      <button
                        className="secondary"
                        disabled={
                          account.status !== "active" ||
                          Boolean(deletingUserId) ||
                          Boolean(activatingUserId)
                        }
                        onClick={() => setPasswordTarget(account)}
                      >
                        重置密码
                      </button>
                    )}
                    <button
                      className="danger ghost"
                      disabled={Boolean(deletingUserId) || Boolean(activatingUserId)}
                      onClick={() => void removeUser(account)}
                    >
                      <Trash2 size={15} /> {deletingUserId === account.id ? "正在删除…" : "删除"}
                    </button>
                  </div>
                ))
            )}
          </div>
        </section>
      ) : (
        <>
          <McpRawUsageCard usage={mcpUsage} />
          <AdminModelCacheUsage />
          <section className="admin-usage-list">
            {loading && !usage ? (
              <LoadingScreen label="正在加载用量…" />
            ) : usage?.users.length === 0 ? (
              <p className="muted">当前没有可展示的用户用量。</p>
            ) : (
              usage?.users.map((accountUsage) => (
                <article className="usage-user-card" key={accountUsage.user_id}>
                  <h2>{accountUsage.email}</h2>
                  <UsageBreakdown usage={accountUsage} compact />
                </article>
              ))
            )}
          </section>
        </>
      )}
      {reviewingGuideId &&
        guides?.guides.find((guide) => guide.definition_id === reviewingGuideId) &&
        (() => {
          const guide = guides.guides.find((entry) => entry.definition_id === reviewingGuideId)!;
          return (
            <GuideApprovalReview
              key={`${guide.definition_id}:${guide.content_sha256}:${guide.review.canary?.traceDigest}`}
              guide={guide.review}
              onClose={() => setReviewingGuideId(null)}
              onApproved={async () => {
                await reload();
                setReviewingGuideId(null);
              }}
            />
          );
        })()}
      {passwordTarget && (
        <AdminPasswordDialog
          account={passwordTarget}
          onClose={() => setPasswordTarget(null)}
          onSubmit={async (nextPassword) => {
            await changeUserPassword(passwordTarget.id, nextPassword);
            await reload();
            setPasswordTarget(null);
          }}
        />
      )}
    </div>
  );
}

function AdminPasswordDialog({
  account,
  onClose,
  onSubmit,
}: {
  account: AuthUser;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("两次输入的新密码不一致");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSubmit(password);
    } catch (cause) {
      setError(messageOf(cause));
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !busy) onClose();
      }}
    >
      <section
        aria-labelledby="admin-password-title"
        aria-modal="true"
        className="modal-card"
        role="dialog"
      >
        <header className="modal-header">
          <div>
            <p className="eyebrow">ACCOUNT SECURITY</p>
            <h2 id="admin-password-title">重置用户密码</h2>
          </div>
          <button
            aria-label="关闭密码重置"
            className="icon-button"
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>
        <p className="muted">
          为 <strong>{account.email}</strong> 设置新密码。保存后，该用户已有登录状态会立即失效。
        </p>
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <label>
            新密码
            <input
              aria-label="重置后的新密码"
              autoComplete="new-password"
              autoFocus
              disabled={busy}
              minLength={12}
              maxLength={128}
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <label>
            确认新密码
            <input
              aria-label="确认重置后的新密码"
              autoComplete="new-password"
              disabled={busy}
              minLength={12}
              maxLength={128}
              required
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          {error && <ErrorBanner message={error} />}
          <div className="modal-actions">
            <button className="secondary" disabled={busy} type="button" onClick={onClose}>
              取消
            </button>
            <button className="primary" disabled={busy || !password || !confirmation} type="submit">
              {busy ? "正在保存…" : "确认重置"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function userRoleLabel(role: UserRole): string {
  return role === "admin" ? "管理员" : "用户";
}

function userStatusLabel(status: AuthUser["status"]): string {
  if (status === "pending") return "待激活";
  if (status === "disabled") return "已停用";
  return "正常";
}

function userGenderLabel(gender: AuthUser["gender"]): string {
  if (gender === "female") return "女";
  if (gender === "male") return "男";
  if (gender === "other") return "其他";
  return "不愿透露";
}

function guideStatusLabel(status?: string): string {
  if (!status) return "未知";
  const labels: Record<string, string> = {
    report_delivery_started: "最终报告",
    approved: "已批准",
    blocked: "已阻断",
    deprecated: "已停用",
    draft: "草稿",
    validated: "已验收",
  };
  return labels[status] ?? status;
}

function UsageCenter() {
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void getMyUsage()
      .then(setUsage)
      .catch((cause) => setError(messageOf(cause)));
  }, []);

  return (
    <div className="usage-shell">
      <header className="usage-header">
        <div>
          <p className="eyebrow">USAGE & BILLING</p>
          <h1>用量与计费</h1>
        </div>
      </header>
      {error && <ErrorBanner message={error} onClose={() => setError("")} />}
      {usage ? <UsageBreakdown usage={usage} /> : !error && <LoadingScreen label="正在加载用量…" />}
    </div>
  );
}

function UsageBreakdown({ usage, compact = false }: { usage: UserUsage; compact?: boolean }) {
  return (
    <div className={`usage-breakdown ${compact ? "compact" : ""}`}>
      <div className="usage-totals">
        <UsageMetric label="总 token" value={usage.total_tokens} />
        <UsageMetric label="模型调用" value={usage.total_model_calls} />
        <UsageMetric label="工具调用" value={usage.total_tool_calls} />
        <UsageMetric label="未报告 token 的调用" value={usage.unreported_model_calls} />
      </div>
      <section className="usage-section">
        <h3>模型用量</h3>
        {usage.models.length === 0 ? (
          <p className="muted">尚无模型用量。</p>
        ) : (
          <div className="usage-table" role="table" aria-label={`${usage.email} 的模型用量`}>
            {usage.models.map((model) => (
              <div
                className="usage-table-row model"
                role="row"
                key={`${model.provider_id}/${model.model_id}`}
              >
                <div>
                  <strong>{model.model_display_name}</strong>
                  <small>
                    {model.provider_id}/{model.model_id}
                  </small>
                </div>
                <span>{formatUsageNumber(model.calls)} 次</span>
                <span>输入 {formatUsageNumber(model.input_tokens)}</span>
                <span>输出 {formatUsageNumber(model.output_tokens)}</span>
                <strong>{formatUsageNumber(model.total_tokens)} token</strong>
                {model.unreported_calls > 0 && (
                  <small>{model.unreported_calls} 次未报告 token</small>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="usage-section">
        <h3>MCP 工具调用</h3>
        {usage.tools.length === 0 ? (
          <p className="muted">尚无工具调用。</p>
        ) : (
          <div className="usage-table" role="table" aria-label={`${usage.email} 的工具用量`}>
            {usage.tools.map((tool) => (
              <div
                className="usage-table-row tool"
                role="row"
                key={`${tool.server_id}/${tool.tool_name}/${tool.function_name}`}
              >
                <div>
                  <strong>
                    {tool.tool_name}.{tool.function_name}
                  </strong>
                  <small>{tool.server_id}</small>
                </div>
                <strong>{formatUsageNumber(tool.calls)} 次</strong>
                <span>成功 {formatUsageNumber(tool.succeeded_calls)}</span>
                <span>失败 {formatUsageNumber(tool.failed_calls)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** 仅展示平台观察到的逻辑调用、提交尝试和传输量，不推算上游费用。 */
function McpRawUsageCard({ usage }: { usage: McpRawUsage | null }) {
  if (!usage) return null;
  return (
    <section className="usage-user-card mcp-raw-usage" aria-label="MCP 原始用量">
      <h2>MCP 服务用量</h2>
      <p className="muted">
        来源：平台实际观察的服务调用与文件传输。耗时包含等待时间，不代表 CPU
        用量。上游费用未报告，本期不计价或扣费。
      </p>
      {!usage.enabled ? (
        <p className="muted">MCP 用量统计尚未启用。</p>
      ) : (
        <>
          <p className="muted">统计时间 {new Date(usage.measured_at).toLocaleString("zh-CN")}</p>
          <div className="usage-totals">
            <UsageMetric label="逻辑调用" value={usage.totals.events} />
            <UsageMetric label="成功" value={usage.totals.succeeded} />
            <UsageMetric label="失败" value={usage.totals.failed} />
            <UsageMetric label="提交尝试" value={usage.totals.external_calls} />
          </div>
          <section className="usage-section">
            <h3>按函数分布</h3>
            {usage.functions.length === 0 ? (
              <p className="muted">尚无 MCP 调用记录。</p>
            ) : (
              <div className="usage-table" role="table" aria-label="MCP 原始用量按函数分布">
                {usage.functions.map((entry) => (
                  <div
                    className="usage-table-row tool"
                    role="row"
                    key={`${entry.service_id}/${entry.function_name}`}
                  >
                    <div>
                      <strong>
                        {entry.service_id} / {entry.function_name}
                      </strong>
                      <small>累计耗时 {formatUsageNumber(entry.wall_time_ms)} ms</small>
                    </div>
                    <strong>{formatUsageNumber(entry.invocations)} 次</strong>
                    <span>成功 {formatUsageNumber(entry.succeeded)}</span>
                    <span>失败 {formatUsageNumber(entry.failed)}</span>
                    <span>提交尝试 {formatUsageNumber(entry.external_calls)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </section>
  );
}

function UsageMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="usage-metric">
      <span>{label}</span>
      <strong>{formatUsageNumber(value)}</strong>
    </div>
  );
}

function formatUsageNumber(value: number): string {
  return new Intl.NumberFormat("zh-CN").format(value);
}

function StatusPill({ value }: { value: string }) {
  return <span className={`status-pill ${value}`}>{guideStatusLabel(value)}</span>;
}

function ErrorBanner({ message, onClose }: { message: string; onClose?: () => void }) {
  return (
    <div className="error-banner" role="alert">
      <span>{message}</span>
      {onClose && (
        <button className="icon-button" aria-label="关闭错误" onClick={onClose}>
          <X size={15} />
        </button>
      )}
    </div>
  );
}

function LoadingScreen({ label }: { label: string }) {
  return (
    <div className="loading-screen">
      <RefreshCw className="spin" />
      <span>{label}</span>
    </div>
  );
}

function TaskLaunchScreen({
  goal,
  attachments = [],
}: {
  goal: string;
  attachments?: ComposerAttachment[];
}) {
  return (
    <div className="task-workspace-layout rail-closed">
      <div className="task-shell">
        <header className="task-header">
          <div>
            <p className="eyebrow">AGENTIC BIOINFORMATICS</p>
            <h1>{provisionalTaskTitle(goal)}</h1>
            <p className="session-identity">正在保存任务…</p>
          </div>
        </header>
        <div className="conversation">
          <div className="timeline">
            <article className="message user">
              <div className="message-meta">
                <span>你</span>
                <span>刚刚</span>
              </div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{goal}</ReactMarkdown>
            </article>
          </div>
          <AssistantActivity label="正在保存任务" />
        </div>
        <div className="composer" aria-label="任务启动中">
          <AutoGrowTextarea
            aria-label="任务目标"
            value=""
            maxRows={8}
            disabled
            readOnly
            placeholder="首轮任务正在启动…"
          />
          <ComposerAttachmentList items={attachments} />
          <button aria-label="正在发送消息" className="primary send" type="button" disabled>
            <RefreshCw className="spin" size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CollaborationModeMenu({
  mode,
  disabled,
  onChange,
}: {
  mode: "default" | "plan";
  disabled: boolean;
  onChange: (mode: "default" | "plan") => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="collaboration-mode-menu">
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="打开协作模式菜单"
        className={`composer-mode-trigger ${mode}`}
        disabled={disabled}
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        <GitBranch aria-hidden="true" size={14} />
        <span>{mode === "plan" ? "计划模式" : "默认执行"}</span>
        <ChevronDown aria-hidden="true" className={open ? "open" : ""} size={13} />
      </button>
      {open && (
        <div aria-label="选择协作模式" className="collaboration-mode-options" role="menu">
          <button
            aria-checked={mode === "default"}
            role="menuitemradio"
            type="button"
            onClick={() => {
              onChange("default");
              setOpen(false);
            }}
          >
            <strong>默认执行</strong>
            <span>直接完成任务，仅需确认动作会中断</span>
          </button>
          <button
            aria-checked={mode === "plan"}
            role="menuitemradio"
            type="button"
            onClick={() => {
              onChange("plan");
              setOpen(false);
            }}
          >
            <strong>计划模式</strong>
            <span>先研究和修改计划，再手动开始执行</span>
          </button>
        </div>
      )}
    </div>
  );
}

function NewConversation({
  options,
  busy,
  goal,
  attachments,
  onFiles,
  onGoalChange,
  onRemoveAttachment,
  onRetryAttachment,
  onSubmit,
  onUploadBlocked,
}: {
  options: RuntimeOptions | null;
  busy: boolean;
  goal: string;
  attachments: ComposerAttachment[];
  onFiles: (files: File[]) => void;
  onGoalChange: (goal: string) => void;
  onRemoveAttachment: (attachment: ComposerAttachment) => void;
  onRetryAttachment: (attachment: ComposerAttachment) => void;
  onSubmit: (
    content: string,
    seriesId: string,
    collaborationMode: "default" | "plan",
  ) => Promise<void>;
  onUploadBlocked: (message: string) => void;
}) {
  const choices = conversationModelChoices(options);
  const defaultChoice = defaultConversationModelChoice(options, choices);
  const [selectedModelValue, setSelectedModelValue] = useState("");
  const [collaborationMode, setCollaborationMode] = useState<"default" | "plan">("default");
  const selectedChoice =
    choices.find((choice) => choice.value === selectedModelValue) ?? defaultChoice;
  const unsettledUploads = attachments.some((item) => item.status !== "uploaded");
  const dropZone = useComposerDropZone({
    disabled: busy || !selectedChoice,
    disabledReason: busy ? "当前正在准备任务，请稍候。" : "当前没有可用模型系列。",
    onBlocked: onUploadBlocked,
    onFiles,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const content = goal.trim();
    if (!content || busy || !selectedChoice || unsettledUploads) {
      return;
    }
    void onSubmit(content, selectedChoice.seriesId, collaborationMode);
  }

  return (
    <section
      {...dropZone.dropZoneProps}
      className={`new-conversation ${dropZone.dragging ? "dragging-files" : ""}`}
    >
      <div className="new-conversation-intro">
        <div className="brand-mark">
          <Bot />
        </div>
        <h1>我们先从哪里开始呢？</h1>
        <p>描述你的研究目标、已有材料，或者一个想验证的问题。</p>
      </div>
      <form className="new-conversation-form" onSubmit={submit}>
        <CollaborationModeMenu
          mode={collaborationMode}
          disabled={busy}
          onChange={setCollaborationMode}
        />
        <ComposerUploadButton
          disabled={busy || !selectedChoice}
          disabledReason={busy ? "当前正在准备任务，请稍候。" : "当前没有可用模型系列。"}
          onFiles={onFiles}
        />
        <AutoGrowTextarea
          aria-label="任务目标"
          autoFocus
          value={goal}
          maxRows={8}
          disabled={busy || !selectedChoice}
          onChange={(event) => onGoalChange(event.target.value)}
          placeholder="给 RNAVoyager 发消息"
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <ComposerAttachmentList
          items={attachments}
          onRemove={onRemoveAttachment}
          onRetry={onRetryAttachment}
        />
        <ConversationModelSelect
          options={options}
          seriesId={selectedChoice?.seriesId ?? null}
          menuDirection="down"
          disabled={busy}
          onChange={(seriesId) => {
            setSelectedModelValue(seriesId);
          }}
        />
        <button
          className="primary new-conversation-send"
          type="submit"
          aria-label="发送消息"
          disabled={busy || !goal.trim() || !selectedChoice || unsettledUploads}
        >
          {busy ? <RefreshCw className="spin" size={18} /> : <ArrowUp size={19} />}
        </button>
      </form>
      <FileDropOverlay active={dropZone.dragging} />
      {!selectedChoice ? <p className="muted">当前模型目录为空。</p> : null}
    </section>
  );
}

function validComposerFiles(
  files: File[],
  policy: RuntimeOptions["workspace_upload"],
  existingCount = 0,
  existingBytes = 0,
): { files: File[]; error: string } {
  const accepted: File[] = [];
  const errors: string[] = [];
  let nextCount = existingCount;
  let nextBytes = existingBytes;
  for (const file of files) {
    if (file.size < 1) {
      errors.push(`${file.name} 是空文件`);
      continue;
    }
    if (file.size > policy.max_file_bytes) {
      errors.push(`${file.name} 超过单文件 ${formatUploadBytes(policy.max_file_bytes)} 上限`);
      continue;
    }
    if (nextCount + 1 > policy.max_input_files) {
      errors.push(`输入文件不能超过 ${policy.max_input_files} 个`);
      continue;
    }
    if (nextBytes + file.size > policy.max_input_total_bytes) {
      errors.push(`输入文件总量不能超过 ${formatUploadBytes(policy.max_input_total_bytes)}`);
      continue;
    }
    accepted.push(file);
    nextCount += 1;
    nextBytes += file.size;
  }
  const distinctErrors = [...new Set(errors)];
  return {
    files: accepted,
    error:
      distinctErrors.length > 0 ? `部分文件未加入：${distinctErrors.slice(0, 3).join("；")}` : "",
  };
}

function inputUploadDisabledReason(
  state: TaskRuntimeState["state"],
  lifecycleStatus: SessionSummary["status"],
): string {
  if (lifecycleStatus === "failed") {
    return "Session 已失败隔离，不能修改输入文件。";
  }
  if (lifecycleStatus === "rotating" || lifecycleStatus === "provisioning") {
    return "Session 正在切换运行环境，请等待重新激活。";
  }
  if (lifecycleStatus === "deleting" || lifecycleStatus === "deleted") {
    return "Session 正在删除或已删除，不能修改输入文件。";
  }
  if (state === "awaiting_plan_start") {
    return "当前计划已绑定输入；请先执行或放弃计划后再上传。";
  }
  if (state === "awaiting_user_input") return "请先回答当前待处理问题。";
  if (state === "awaiting_action_approval") return "请先处理当前待审批动作。";
  if (state === "stop_failed") return "请先重试停止任务。";
  if (state === "stopping") return "任务正在停止，请稍候。";
  return "当前 turn 已绑定输入；请等待任务结束或先停止任务。";
}

function formatUploadBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
  }
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GiB`;
}

function provisionalTaskTitle(content: string): string {
  return content.trim().split(/\s+/u).join(" ").slice(0, 120).trimEnd();
}

function mergeTaskState(current: TaskRuntimeState, next: TaskRuntimeState): TaskRuntimeState {
  const events = new Map<string, TaskEvent>();
  for (const event of [...current.events, ...next.events]) {
    events.set(event.event_id, event);
  }
  return {
    ...next,
    session_status: next.session_status ?? current.session_status,
    events: [...events.values()].sort((left, right) => left.cursor - right.cursor),
    events_cursor: next.next_cursor ?? next.events_cursor,
  };
}

function resultDocumentRevisions(
  events: readonly TaskEvent[],
  documentId: string | null,
  current: ResultDocument | null,
): ResultDocument[] {
  if (!documentId) return [];
  const revisions = new Map<number, ResultDocument>();
  for (const event of events) {
    const document = event.result_document;
    if (document?.documentId === documentId) revisions.set(document.revision, document);
  }
  if (current?.documentId === documentId) revisions.set(current.revision, current);
  return [...revisions.values()].sort((left, right) => left.revision - right.revision);
}

function resultSelectionReference(selection: ResultSelection): string {
  const values = selection.values
    .map(({ label, value }) => `${compactReferenceText(label, 80)}=${resultSelectionScalar(value)}`)
    .join("；");
  return boundedReferenceText(
    [
      "参考以下已选计算结果（它是计算证据，不是实验验证）：",
      `- 文档：${compactReferenceText(selection.documentTitle, 200)}（修订 ${String(selection.revision)}）`,
      `- 区块：${compactReferenceText(selection.title, 200)}`,
      `- 来源：${compactReferenceText(selection.sourceObservationId, 128)}${compactReferenceText(selection.sourcePath, 512)}，第 ${String(selection.sourceIndex + 1)} ${selection.sourceIndexKind === "item" ? "项" : "行"}${selection.sourceColumnIndex === undefined ? "" : `，第 ${String(selection.sourceColumnIndex + 1)} 列`}`,
      `- 数据：${values}`,
    ].join("\n"),
    4_096,
  );
}

function resultSelectionScalar(value: ResultSelection["values"][number]["value"]): string {
  if (typeof value === "string") return JSON.stringify(compactReferenceText(value, 240));
  if (value === null) return "null";
  return String(value);
}

function compactReferenceText(value: string, maximumCharacters: number): string {
  const normalized = value.replaceAll(/\s+/gu, " ").trim();
  return boundedReferenceText(normalized, maximumCharacters);
}

function boundedReferenceText(value: string, maximumCharacters: number): string {
  const characters = Array.from(value);
  return characters.length <= maximumCharacters
    ? value
    : `${characters.slice(0, maximumCharacters - 1).join("")}…`;
}

function compactRailViewport(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(COMPACT_RAIL_QUERY).matches;
}

function findPendingQuestion(events: TaskEvent[], requestId: string | null): TaskEvent | null {
  const questions = [...events]
    .reverse()
    .filter((event) => event.event_type === "request_user_input");
  if (!requestId) return questions[0] ?? null;
  return (
    questions.find((event) => event.payload?.request_id === requestId) ??
    questions.find((event) => event.payload?.request_id === undefined) ??
    null
  );
}

function findPendingActionApproval(events: TaskEvent[]): PendingActionApproval | null {
  const resolved = new Set(
    events.flatMap((event) => {
      if (event.event_type !== "action_approved" && event.event_type !== "action_denied") return [];
      const key = actionApprovalEventKey(event);
      return key ? [key] : [];
    }),
  );
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event.event_type !== "action_approval_requested") continue;
    const approval = actionApprovalFromEvent(event);
    if (approval && !resolved.has(actionApprovalKey(approval))) return approval;
  }
  return null;
}

function actionApprovalFromEvent(event: TaskEvent): PendingActionApproval | null {
  const payload = event.payload ?? {};
  const actionId = typeof payload.action_id === "string" ? payload.action_id : "";
  const requestDigest = typeof payload.request_digest === "string" ? payload.request_digest : "";
  const kind = typeof payload.kind === "string" ? payload.kind : "";
  const summary = typeof payload.public_summary === "string" ? payload.public_summary.trim() : "";
  const reason = typeof payload.reason === "string" ? payload.reason.trim() : "";
  const requestedCharge = payload.requested_charge;
  const publicDetails = payload.public_details;
  if (
    !actionId ||
    !/^[0-9a-f]{64}$/u.test(requestDigest) ||
    !kind ||
    !summary ||
    !reason ||
    !requestedCharge ||
    typeof requestedCharge !== "object" ||
    Array.isArray(requestedCharge)
  ) {
    return null;
  }
  return {
    actionId,
    requestDigest,
    kind,
    summary,
    reason,
    requestedCharge: requestedCharge as Record<string, unknown>,
    details:
      publicDetails && typeof publicDetails === "object" && !Array.isArray(publicDetails)
        ? (publicDetails as Record<string, unknown>)
        : {},
  };
}

function actionApprovalKey(approval: PendingActionApproval): string {
  return `${approval.actionId}:${approval.requestDigest}`;
}

function actionApprovalEventKey(event: TaskEvent): string | null {
  const actionId = event.payload?.action_id;
  const requestDigest = event.payload?.request_digest;
  if (
    typeof actionId !== "string" ||
    typeof requestDigest !== "string" ||
    !/^[0-9a-f]{64}$/u.test(requestDigest)
  ) {
    return null;
  }
  return `${actionId}:${requestDigest}`;
}

function latestTaskFailure(events: TaskEvent[]): TaskEvent | null {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    if (events[index].event_type === "task_failed") return events[index];
  }
  return null;
}

function hiddenRoutineExecutionProgress(event: TaskEvent): boolean {
  if (event.event_type === "action_progress") return true;
  if (event.event_type === "action_granted" || event.event_type === "action_started") {
    return true;
  }
  if (event.event_type !== "action_completed" && event.event_type !== "observation_recorded") {
    return false;
  }
  const payload = event.payload ?? {};
  if (
    payload.action_failure &&
    typeof payload.action_failure === "object" &&
    !Array.isArray(payload.action_failure)
  ) {
    return false;
  }
  if (
    typeof payload.status === "string" &&
    USER_ATTENTION_EXECUTION_STATUSES.has(payload.status.toLowerCase())
  ) {
    return false;
  }
  const progress = payload.job_progress;
  if (progress && typeof progress === "object" && !Array.isArray(progress)) {
    const status = (progress as Record<string, unknown>).status;
    if (typeof status === "string" && USER_ATTENTION_EXECUTION_STATUSES.has(status.toLowerCase())) {
      return false;
    }
  }
  return true;
}

function taskFailureMessage(event: TaskEvent): string {
  const recorded = event.message?.trim() || event.content?.trim();
  if (recorded) return recorded;

  const payload = event.payload ?? {};
  const failureCode = typeof payload.failure_code === "string" ? payload.failure_code.trim() : "";
  const failureStage =
    typeof payload.failure_stage === "string" ? payload.failure_stage.trim() : "";
  const exceptionType =
    typeof payload.exception_type === "string" ? payload.exception_type.trim() : "";
  const reason = typeof payload.reason === "string" ? payload.reason.trim() : "";
  const stageLabel =
    failureStage === "planning" ? "规划" : failureStage === "execution" ? "执行" : failureStage;
  const details = [
    failureCode && `- 错误码：\`${failureCode}\``,
    stageLabel && `- 阶段：${stageLabel}`,
    exceptionType && `- 异常类型：\`${exceptionType}\``,
    reason && `- 原因：${reason}`,
  ].filter(Boolean);

  if (details.length === 0) {
    return "任务执行失败，但服务端没有提供可展示的详细原因。请稍后重试；若问题持续出现，请联系管理员并提供会话 ID。";
  }
  return ["任务执行失败。", "", "排查信息：", "", ...details].join("\n");
}

function taskFailureStageLabel(stage: string): string {
  return (
    new Map([
      ["planning", "规划"],
      ["execution", "执行"],
      ["scheduler", "任务调度"],
      ["model_decision", "模型决策"],
      ["model_usage", "记录模型用量"],
      ["authorization_envelope", "准备任务授权"],
      ["action_request", "准备动作请求"],
      ["action_grant", "签发动作授权"],
      ["mcp_discovery", "读取工具能力"],
      ["mcp_prepare", "准备工具调用"],
      ["mcp_run", "执行工具调用"],
      ["mcp_cleanup", "清理工具调用"],
      ["mcp_close", "关闭工具连接"],
      ["mcp_lease_release", "释放工具连接租约"],
      ["runtime_activity_release", "释放运行资源租约"],
      ["usage_reservation", "预留动作预算"],
      ["usage_release", "释放动作预算"],
      ["task_event_persistence", "记录任务状态"],
    ]).get(stage) ?? ""
  );
}

interface NormalisedQuestion {
  id: string;
  prompt: string;
  options: Array<{ label: string; description: string }>;
}

function normaliseQuestions(event: TaskEvent | null): NormalisedQuestion[] {
  const payloadQuestions = Array.isArray(event?.payload?.questions)
    ? event.payload.questions.slice(0, 3)
    : [];
  const questions = payloadQuestions.flatMap((value, index): NormalisedQuestion[] => {
    if (!value || typeof value !== "object") return [];
    const item = value as Record<string, unknown>;
    const prompt = typeof item.question === "string" ? item.question.trim() : "";
    if (!prompt) return [];
    const id = typeof item.id === "string" && item.id.trim() ? item.id : `answer_${index + 1}`;
    const rawOptions = Array.isArray(item.options) ? item.options : [];
    const options = rawOptions.flatMap((option): NormalisedQuestion["options"] => {
      if (!option || typeof option !== "object") return [];
      const candidate = option as Record<string, unknown>;
      if (typeof candidate.label !== "string" || !candidate.label.trim()) return [];
      return [
        {
          label: candidate.label.trim(),
          description:
            typeof candidate.description === "string" ? candidate.description.trim() : "",
        },
      ];
    });
    return [{ id, prompt, options }];
  });
  if (questions.length > 0) return questions;
  return [
    {
      id: "answer",
      prompt: event?.message?.trim() || "请补充任务信息。",
      options: [],
    },
  ];
}

function eventLabel(eventType: string, payload?: Record<string, unknown>): string {
  if (eventType === "observation_recorded" && payload?.action_failure) {
    return "工具异常";
  }
  if (eventType === "observation_recorded" && payload?.kind === "web_search") {
    return "网页搜索";
  }
  const labels: Record<string, string> = {
    task_failed: "任务失败",
    task_interrupted: "任务中断",
    task_completed: "任务完成",
    task_cancelled: "任务取消",
    task_stopping: "正在停止",
    task_stopped: "任务已停止",
    task_stop_failed: "停止失败",
    task_resumed: "继续任务",
    authorization_established: "执行授权",
    plan_execution_started: "开始执行",
    plan_discarded: "计划",
    working_plan_created: "任务规划",
    working_plan_updated: "规划更新",
    action_approval_requested: "动作审批",
    action_approved: "动作审批",
    action_denied: "动作审批",
    guide_changed: "Guide",
    action_started: "执行",
    action_completed: "执行",
    observation: "观察",
    observation_recorded: "工具进度",
  };
  return labels[eventType] ?? "RNAVoyager";
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function messageOf(cause: unknown): string {
  if (cause instanceof DOMException && cause.name === "AbortError") {
    return "请求已取消，请重试。";
  }
  if (cause instanceof TypeError && /failed to fetch|networkerror/i.test(cause.message)) {
    return "网络连接失败，请检查连接后重试。";
  }
  return cause instanceof Error ? cause.message : "操作失败，请重试。";
}
